"use client"
import { API_ROOT, WEB_ROOT, SECURE_COOKIES } from "@/components/api/api-config";
import { Cookies } from "react-cookie";

import axios from "axios";
import { jwtDecode as jwt_decode } from "jwt-decode";
import { v1 as uuidv1 } from "uuid";

import geohash from "ngeohash";
import moment from "moment-timezone";

function API(configArg) {
  const config = configArg ?? {
    apiUrl: API_ROOT,
    webUrl: WEB_ROOT,
    secureCookies: SECURE_COOKIES
  };
  const root = typeof window !== 'undefined' ? window : global;
  const referrer = root && root.location && root.location.href;
  if (referrer) {
    const fragment = referrer.replace(config.webUrl, "");
    axios.defaults.headers.common["X-Referer"] = fragment;
  }

  let cookies = new Cookies();
  let apiUrl = config.apiUrl;

  var transactorGUID = getTransactorGUID();
  axios.defaults.headers.common["X-Transactor-ID"] = transactorGUID;

  function getTransactorGUID() {
    var transactorGUID = cookies.get("transactorID");
    if (!transactorGUID) {
      transactorGUID = uuidv1();
      var aYearFromNow = new Date();
      aYearFromNow.setFullYear(aYearFromNow.getFullYear() + 1);
      cookies.set("transactorID", transactorGUID, {
        expires: aYearFromNow,
        secure: config.secureCookies
      });
    }
    return transactorGUID;
  }

  async function setGeoHashHeader() {
    // console.log("setGeoHashHeader");
    await getGeoHash()
      .then(geoHashVal => {
        axios.defaults.headers.common["X-Geohash"] = geoHashVal;
      })
      .catch(error => {
        // NOOP, move along
      });
  }

  function getGeoHash() {
    if (typeof window !== "undefined" && window.top !== window.self) {
      // This code is running inside an iframe
      reject(null);
    } else {
      // This code is not running inside an iframe
      return new Promise(async function(resolve, reject) {
        if (typeof window !== "undefined") {
          var locationEnabled = localStorage.getItem("LOCATION_ENABLED");
          if (locationEnabled && locationEnabled === "true") {
            if (window.navigator && window.navigator.geolocation) {
              // console.log("about to call getCurrentPosition");
              await window.navigator.geolocation.getCurrentPosition(
                position => {
                  // console.log(position);
                  const value = geohash.encode(
                    position.coords.latitude,
                    position.coords.longitude
                  );
                  // console.log(value);
                  resolve(value);
                },
                error => {
                  // console.log(error);
                  reject(error);
                },
                {
                  enableHighAccuracy: true,
                  timeout: 3000,
                  maximumAge: 0
                }
              );
            } else {
              // console.log("geolocation unavailable");
              reject(null);
            }
          } else {
            reject(null);
          }
        } else {
          reject(null);
        }
      });
    }
  }

  function getAuthentication() {
    return cookies.get("authentication", { doNotParse: true });
  }

  function useDefaultAuthenticationToken() {
    let storedAuth = getAuthentication();
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      return "Bearer " + authentication.token;
    } else {
      return null;
    }
  }

  function infoCheck() {
    return new Promise(function(resolve, reject) {
      axios
        .get(apiUrl + "/manage/info")
        .then(info => {
          resolve(info.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function healthCheck() {
    return new Promise(function(resolve, reject) {
      axios
        .get(apiUrl + "/manage/health")
        .then(info => {
          resolve(info.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getJwtToken(username, password, community, callback) {
    let body = {
      username: username,
      password: password,
      community: community
    };
    const url = apiUrl + "/api/token";
    axios
      .post(url, body)
      .then(response => {
        var data = response.data;
        var defaultUrl = response.headers["x-default-url"];
        var decoded = jwt_decode(data.token);
        let authentication = {
          subject: decoded.sub,
          profileName: decoded.profile,
          type: decoded.type,
          scope: decoded.scope,
          expires: decoded.exp,
          token: data.token
        };
        cookies.set("authentication", JSON.stringify(authentication), {
          expires: new Date(authentication.expires * 1000),
          secure: config.secureCookies,
          path: "/"
        });

        let result = {
          success: true,
          payload: decoded,
          defaultUrl: defaultUrl
        };
        axios.defaults.headers.common["Authorization"] = "Bearer " + data.token; // for all requests

        callback(result);
      })
      .catch(error => {
        console.log(error);
        let result = {
          success: false,
          error: error
        };
        callback(result);
      });
  }

  function isNonExpiredToken(token) {
    var decoded = jwt_decode(token);
    if (decoded) {
      var currentTimestamp = moment().unix();
      return decoded.exp > currentTimestamp;
    } else {
      return false;
    }
  }

  function isValidTokenForScope(token, resourceName, scope) {
    var decoded = jwt_decode(token);
    if (decoded) {
      var currentTimestamp = moment().unix();
      return (
        decoded.profile === resourceName &&
        decoded.scope === scope &&
        decoded.exp > currentTimestamp
      );
    } else {
      return false;
    }
  }

  function isValidAuthentication() {
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      var currentTimestamp = moment().unix();
      if (authentication.exp > currentTimestamp) {
        return true;
      } else {
        // token expired
        return false;
      }
    } else {
      // no token stored
      return false;
    }
  }

  function clearAuthentication() {
    cookies.remove("authentication", { secure: config.secureCookies });
    axios.defaults.headers.common["Authorization"] = "Bearer "; // clear until re-added
  }

  function getAnonymousAuthenticationToken(callback) {
    getAnonymousAuthenticationTokenFromHost(apiUrl, callback);
  }

  function getAnonymousAuthenticationTokenFromHost(
    host,
    callback,
    forceReauth
  ) {
    var anonAuth = cookies.get("anon_authentication", { doNotParse: true });
    if (anonAuth == null || forceReauth) {
      axios
        .post(host + "/api/token/anon")
        .then(response => {
          var data = response.data;
          var decoded = jwt_decode(data.token);
          let authentication = {
            expires: decoded.exp,
            token: data.token
          };
          cookies.set("anon_authentication", JSON.stringify(authentication), {
            expires: new Date(authentication.expires * 1000),
            secure: config.secureCookies
          });
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + response.data.token; // for all requests
          callback();
        })
        .catch(error => {
          console.log("API calls require at least anonymous authentication");
        });
    } else {
      var anonAuthentication = JSON.parse(anonAuth);
      var currentTimestamp = moment().unix();
      if (anonAuthentication.expires > currentTimestamp) {
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + anonAuthentication.token; // for all requests
        callback();
      } else {
        cookies.remove("anon_authentication");
        getAnonymousAuthenticationTokenFromHost(host, callback, forceReauth);
      }
    }
  }

  // function getAnonymousAuthenticationToken(callback) {
  //   if (anonToken == null) {
  //     axios
  //       .post(apiUrl + "/api/token/anon")
  //       .then(response => {
  //         axios.defaults.headers.common["Authorization"] =
  //           "Bearer " + response.data.token; // for all requests
  //         anonymouslyAuthenticated = true;
  //         anonToken = response.data.token;
  //         callback();
  //       })
  //       .catch(error => {
  //         console.log("API calls require at least anonymous authentication");
  //       });
  //   } else {
  //     callback();
  //   }
  // }

  // function isValidAnonymousToken() {

  // }

  function emailAddressAvailable(emailAddress) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const payload = {
          email: emailAddress
        };
        const url = apiUrl + "/api/profiles/email/validate";
        axios
          .post(url, payload)
          .then(success => {
            resolve(true);
          })
          .catch(error => {
            resolve(false);
          });
      });
    });
  }

  function profileNameAvailable(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const payload = {
          name: profileName
        };
        const url = apiUrl + "/api/profiles/name/validate";
        axios
          .post(url, payload)
          .then(success => {
            resolve({ success: true });
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getAuthenticatedProfileForEdit() {
    return new Promise(function(resolve, reject) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + authentication.token;
        var profileName = authentication.profileName;

        axios
          .get(apiUrl + "/api/profiles/" + profileName + "/edit")
          .then(prof => {
            resolve(prof.data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        resolve(null);
      }
    });
  }

  function checkForProfile(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .head(apiUrl + "/api/profiles/" + profileName)
          .then(() => {
            resolve({ success: true });
          })
          .catch(() => {
            reject({ success: false });
          });
      });
    });
  }

  function checkForCommunityContactByEmail(name, emailAddress) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let base64 = new Buffer(emailAddress).toString("base64");
        let url = `${apiUrl}/api/profiles/${name}/community/contacts/e/${base64}`;
        axios
          .head(url)
          .then(() => {
            resolve({ success: true });
          })
          .catch(error => {
            console.log(
              "checkForCommunityContactByEmail error",
              error,
              error.response
            );
            reject({ success: false, code: error.response.status });
          });
      });
    });
  }

  async function getProfile(profileName) {
    // await setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profiles/" + profileName)
          .then(prof => {
            resolve(prof.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileFromGPID(gpid) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profiles/gpid/" + gpid)
          .then(prof => {
            resolve(prof.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  async function getProfileSummaryMap(profileName) {
    // await setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profiles/" + profileName + "/map")
          .then(prof => {
            resolve(prof.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  async function getProfileSummaryMapWithToken(
    profileName,
    accessToken,
    tokenType
  ) {
    return new Promise(function(resolve, reject) {
      let token = tokenType + " " + accessToken;
      axios
        .get(apiUrl + "/api/profiles/" + profileName + "/map", {
          headers: { Authorization: token }
        })
        .then(map => {
          resolve(map.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function queryProfiles(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/q";
        axios
          .post(url, payload)
          .then(profiles => {
            resolve(profiles.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function profileAutocomplete(name, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${name}/autocomplete`;
        axios
          .post(url, payload)
          .then(predictions => {
            resolve(predictions.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function profileCommunityMemberAutocomplete(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/community/members/autocomplete";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(predictions => {
          resolve(predictions.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function profileCommunityMemberOptions(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl + "/api/profiles/" + name + "/community/members/options";
        axios
          .get(url, { headers: { Authorization: token } })
          .then(predictions => {
            resolve(predictions.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileCategories(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profiles/" + profileName + "/categories")
          .then(categories => {
            resolve(categories.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getAllCategories(root) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(
            apiUrl + "/api/categories" + (root == null ? "" : "?root=" + root)
          )
          .then(categories => {
            resolve(categories.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getFlattenedCategories() {
    // var categoriesETAG = cookies.get("ur_categories_ETAG");
    // var headers =
    //   categoriesETAG ? { "If-None-Match": categoriesETAG } : {};
    var headers = {};
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/categories/flat", { headers: headers })
          .then(response => {
            var etag = response.headers["etag"];
            cookies.set("ur_categories_ETAG", etag, {
              secure: config.secureCookies,
              path: "/"
            });
            // TODO store response so it can be reused
            resolve(response.data);
          })
          .catch(error => {
            // TODO handle 304
            reject(error);
          });
      });
    });
  }

  function getProfileTypes() {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profile_types")
          .then(types => {
            resolve(types.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileTypeGroups() {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profile_types/groups")
          .then(groups => {
            resolve(groups.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getFlattenedProfileTypes() {
    // var typesETAG = cookies.get("ur_types_ETAG");
    // var headers =
    //   typesETAG ? { "If-None-Match": typesETAG } : {};
    var headers = {};
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profile_types/flat", { headers: headers })
          .then(response => {
            var etag = response.headers["etag"];
            cookies.set("ur_types_ETAG", etag, {
              secure: config.secureCookies,
              path: "/"
            });
            // TODO store response so it can be reused
            resolve(response.data);
          })
          .catch(error => {
            // TODO handle 304 when ETAG is present
            reject(error);
          });
      });
    });
  }

  function getFlattenedAmenities() {
    // var amenitiesETAG = cookies.get("ur_amenities_ETAG");
    // var headers =
    //   amenitiesETAG ? { "If-None-Match": amenitiesETAG } : {};
    var headers = {};
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/amenities/flat", { headers: headers })
          .then(response => {
            var etag = response.headers["etag"];
            cookies.set("ur_amenities_ETAG", etag, {
              secure: config.secureCookies,
              path: "/"
            });
            // TODO store response so it can be reused
            resolve(response.data);
          })
          .catch(error => {
            // TODO handle 304 when ETAG is present
            reject(error);
          });
      });
    });
  }

  function getFlattenedPAUAmenities() {
    // var amenitiesETAG = cookies.get("ur_amenities_ETAG");
    // var headers =
    //   amenitiesETAG ? { "If-None-Match": amenitiesETAG } : {};
    var headers = {};
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/pau_amenities/flat", { headers: headers })
          .then(response => {
            var etag = response.headers["etag"];
            cookies.set("ur_pau_amenities_ETAG", etag, {
              secure: config.secureCookies,
              path: "/"
            });
            // TODO store response so it can be reused
            resolve(response.data);
          })
          .catch(error => {
            // TODO handle 304 when ETAG is present
            reject(error);
          });
      });
    });
  }

  function getVendorTypes() {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/vendor_types")
          .then(types => {
            resolve(types.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getServiceCategories() {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/service_categories")
          .then(types => {
            resolve(types.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getImageBase64(imageId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/files/" + imageId;
        axios
          .get(url, {
            responseType: "arraybuffer"
          })
          .then(response => {
            var base64 = Buffer.from(response.data, "binary").toString(
              "base64"
            );
            resolve("data:image/jpg;base64," + base64);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getImage(imageId, maxWidth, maxHeight, brightness = 1.0) {
    return new Promise(function(resolve, reject) {
      // getAnonymousAuthenticationToken( () => {
      let params = "";
      if (maxWidth && maxHeight) {
        params = `?w=${maxWidth}&h=${maxHeight}&resize=true&b=${brightness}`;
      } else if (maxWidth) {
        params = `?w=${maxWidth}&resize=true&b=${brightness}`;
      }
      const url = apiUrl + "/api/files/" + imageId + params;
      resolve(url);
    });
  }

  function getResponsiveImage(imageId, size) {
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/files/" + imageId + "/" + size;
      resolve(url);
    });
  }

  function getSquareImage(imageId, size) {
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/files/" + imageId + "/sq/" + size;
      resolve(url);
    });
  }

  function getFile(fileId, extension) {
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/files/" + fileId + "." + extension;
      resolve(url);
    });
  }

  function geoQueryEvents(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/events/q";
        axios
          .post(url, payload)
          .then(events => {
            resolve(events.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function listEventsAtLocation(locationId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/events/at/" + locationId;
        axios
          .get(url)
          .then(events => {
            resolve(events.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getAvailableEventTags(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/events/tags";
        axios
          .get(url)
          .then(tags => {
            resolve(tags.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function queryEvents(
    profileName,
    startDate,
    endDate,
    locationId,
    max,
    offset
  ) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let body = {};
        if (profileName != null) {
          body["profileName"] = profileName;
        }
        if (startDate != null) {
          body["startDate"] = startDate;
        }
        if (endDate != null) {
          body["endDate"] = endDate;
        }
        if (locationId != null) {
          body["locationId"] = locationId;
        }
        if (max != null) {
          body["max"] = max;
        }
        if (offset != null) {
          body["offset"] = offset;
        }
        const url = apiUrl + "/api/events/q";
        axios
          .post(url, body)
          .then(events => {
            resolve(events.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  async function getEventWithToken(
    profileName,
    eventId,
    accessToken,
    tokenType
  ) {
    return new Promise(function(resolve, reject) {
      let token = tokenType + " " + accessToken;
      axios
        .get(apiUrl + "/api/profiles/" + profileName + "/events/" + eventId, {
          headers: { Authorization: token }
        })
        .then(event => {
          resolve(event.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  async function getEvent(profileName, eventId, utmParams) {
    // await setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl + "/api/profiles/" + profileName + "/events/" + eventId;
        var headers = utmParams
          ? { headers: { "X-UTM-Params": utmParams } }
          : null;
        var eventCall = headers ? axios.get(url, headers) : axios.get(url);
        eventCall
          .then(event => {
            resolve(event.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  async function getEventByGroupTag(profileName, groupTag) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl + "/api/profiles/" + profileName + "/events/tagged/" + groupTag;
        axios.get(url)
          .then(event => {
            resolve(event.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getPrintableEventMap(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/printable";
      var eventCall = axios.get(url, { headers: { Authorization: token } });
      eventCall
        .then(eventMap => {
          resolve(eventMap.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getEventMap(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/map";
        var eventCall = axios.get(url);
        eventCall
          .then(eventMap => {
            resolve(eventMap.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getEventMapSpacePlan(profileName, eventId, spacePlanId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/map/space_plans/" +
          spacePlanId;
        var eventCall = axios.get(url);
        eventCall
          .then(spacePlan => {
            resolve(spacePlan.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function updateExistingEventMap(profileName, eventId, payload) {
    // console.log("API updateExistingEventMap", profileName, eventId, payload);
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl + "/api/profiles/" + profileName + "/events/" + eventId + "/map";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getEventMapPoints(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/map/points";
        var eventCall = axios.get(url);
        eventCall
          .then(mapPoints => {
            resolve(mapPoints.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getEventMapParticipants(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/map/participants";
        var eventCall = axios.get(url);
        eventCall
          .then(mapPoints => {
            resolve(mapPoints.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createNewEventMapPoint(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/points";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          // console.log("API createNewMapPoint response", response);
          let location = response.headers["location"];
          let pointId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: pointId
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingEventMapPoint(profileName, eventId, pointId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/points/" +
        pointId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingEventMapPoint(profileName, eventId, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/points/" +
        pointId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getEventMapAmenities(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/map/amenities";
        var eventCall = axios.get(url);
        eventCall
          .then(mapAmenities => {
            resolve(mapAmenities.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createNewEventMapAmenity(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/amenities";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          // console.log("API createNewMapPoint response", response);
          let location = response.headers["location"];
          let pointId = parseIdFromLocation(location);
          let result = {
            success: true,
            data: response.data,
            id: pointId
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingEventMapAmenity(
    profileName,
    eventId,
    pointId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/amenities/" +
        pointId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingEventMapAmenity(profileName, eventId, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/amenities/" +
        pointId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  // TODO: confirm endpoint path with backend — placeholder returns a URL string
  function getEventQrCode(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/qr";
        var call = axios.get(url, { responseType: "blob" });
        call
          .then(response => {
            resolve(URL.createObjectURL(response.data));
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getEventGalleryItems(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/gallery";
        var eventCall = axios.get(url);
        eventCall
          .then(galleryItems => {
            resolve(galleryItems.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function submitEventGalleryItem(
    profileName,
    eventId,
    payload,
    overrideToken
  ) {
    var token = overrideToken;
    if (!token) {
      var storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = authentication.token;
      }
    }
    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      var url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/gallery";
      axios
        .post(url, payload, { headers: { Authorization: tokenHeader } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getEventGalleryItemSubmissions(profileName, eventId) {
    // setGeoHashHeader();
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/gallery_submissions";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function approveSubmittedEventGalleryItem(
    profileName,
    eventId,
    galleryItemId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/gallery_submissions/" +
        galleryItemId +
        "/approve";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          let result = {
            success: true,
            notificationId: notificationId
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function rejectSubmittedEventGalleryItem(
    profileName,
    eventId,
    galleryItemId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/gallery_submissions/" +
        galleryItemId +
        "/reject";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          let result = {
            success: true,
            notificationId: notificationId
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteEventGalleryItem(profileName, eventId, galleryItemId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/gallery/" +
        galleryItemId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listExistingEventMapRoutes(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/events/" +
          eventId +
          "/map/routes";
        axios
          .get(url)
          .then(routes => {
            resolve(routes.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createNewEventMapRoute(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/routes";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          // console.log("API createNewMapPoint response", response);
          let location = response.headers["location"];
          let pointId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: pointId,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingEventMapRoute(profileName, eventId, routeId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/routes/" +
        routeId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingEventMapRoute(profileName, eventId, routeId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/map/routes/" +
        routeId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function signUpToVolunteerForEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/volunteers";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function logTimeForVolunteerEvent(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    payload["timezone"] = timezone;
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/volunteer_log";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getEventVolunteerLog(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    payload["timezone"] = timezone;
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/volunteer_log";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function logTimeForVolunteerManagementAsAdmin(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    payload["timezone"] = timezone;
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/volunteer_management/checkins";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listVolunteerManagementCheckins(
    profileName,
    payload,
    page = 0,
    max = 20
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let params = "?";
      if (max) {
        params += "max=" + max;
      }
      if (page) {
        if (params !== "?") {
          params += "&";
        }
        params += "page=" + page;
      }
      if (params === "?") {
        // no params
        params = "";
      }

      let url = `${apiUrl}/api/profiles/${profileName}/volunteer_management/checkins${params}`;
      axios
        .get(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function viewEventStatistics(profileName, eventId, days) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/stats";
      if (days) {
        url = url + "?days=" + days;
      }
      axios
        .get(url, { headers: { Authorization: token } })
        .then(stats => {
          resolve(stats.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function viewPostStatistics(profileName, postId, days) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl + "/api/profiles/" + profileName + "/posts/" + postId + "/stats";
      if (days) {
        url = url + "?days=" + days;
      }
      axios
        .get(url, { headers: { Authorization: token } })
        .then(stats => {
          resolve(stats.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function viewProductStatistics(profileName, productId, days) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/products/" +
        productId +
        "/stats";
      if (days) {
        url = url + "?days=" + days;
      }
      axios
        .get(url, { headers: { Authorization: token } })
        .then(stats => {
          resolve(stats.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function listProfileEvents(profileName, page = 0, max = 20) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profiles/" + profileName + "/events")
          .then(event => {
            resolve(event.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function addProfileEventParticipant(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/participants";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let itemId = parseIdFromLocation(location);
          let result = {
            data: response.data,
            id: itemId
          };
          resolve(result);
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function editProfileEventParticipant(
    profileName,
    eventId,
    participantId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/participants/" +
        participantId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            data: response.data,
            id: participantId
          };
          resolve(result);
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function removeProfileEventParticipant(profileName, eventId, participantId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/participants/" +
        participantId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function listProfileEventParticipants(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(
            apiUrl +
              "/api/profiles/" +
              profileName +
              "/events/" +
              eventId +
              "/participants"
          )
          .then(participants => {
            resolve(participants.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function geoQueryMaps(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .post(apiUrl + "/api/maps/q", payload)
          .then(maps => {
            resolve(maps.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function listProfileMaps(profileName) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/profiles/" + profileName + "/maps")
          .then(maps => {
            resolve(maps.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function listPrivateMaps(profileName) {
    // setGeoHashHeader();
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/maps/private";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(maps => {
          resolve(maps.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function listPublicAndPrivateMaps(profileName) {
    // setGeoHashHeader();
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/maps/all";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(maps => {
          resolve(maps.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getProfileMapWithToken(profileName, mapId, accessToken, tokenType) {
    return new Promise(function(resolve, reject) {
      let token = tokenType + " " + accessToken;
      axios
        .get(apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId, {
          headers: { Authorization: token }
        })
        .then(map => {
          resolve(map.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getProfileMapPointsWithToken(
    profileName,
    mapId,
    accessToken,
    tokenType
  ) {
    return new Promise(function(resolve, reject) {
      let token = tokenType + " " + accessToken;
      axios
        .get(
          apiUrl +
            "/api/profiles/" +
            profileName +
            "/maps/" +
            mapId +
            "/points",
          {
            headers: { Authorization: token }
          }
        )
        .then(map => {
          resolve(map.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function submitProfileMapPoint(profileName, mapId, payload, overrideToken) {
    var token = overrideToken;
    if (!token) {
      var storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = authentication.token;
      }
    }
    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      var url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/submissions";
      axios
        .post(url, payload, { headers: { Authorization: tokenHeader } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getProfileMapSubmissions(profileName, mapId) {
    // setGeoHashHeader();
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/submissions";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function approveSubmittedMapPoint(profileName, mapId, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/submissions/" +
        pointId +
        "/approve";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          let result = {
            success: true,
            notificationId: notificationId
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function rejectSubmittedMapPoint(profileName, mapId, pointId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/submissions/" +
        pointId +
        "/reject";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          let result = {
            success: true,
            notificationId: notificationId
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function submitProfileMapComment(profileName, mapId, pointId, payload) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }
    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      var url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/points/" +
        pointId +
        "/comments";
      axios
        .post(url, payload, { headers: { Authorization: tokenHeader } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getProfileMapCommentSubmissions(profileName, mapId) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }
    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      var url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/comments";
      axios
        .get(url, { headers: { Authorization: tokenHeader } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function approveProfileMapCommentSubmission(
    profileName,
    mapId,
    pointId,
    commentId
  ) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }
    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      var url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/points/" +
        pointId +
        "/comments/" +
        commentId +
        "/approve";
      axios
        .post(url, {}, { headers: { Authorization: tokenHeader } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function rejectProfileMapCommentSubmission(
    profileName,
    mapId,
    pointId,
    commentId
  ) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }
    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      var url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/points/" +
        pointId +
        "/comments/" +
        commentId +
        "/reject";
      axios
        .post(url, {}, { headers: { Authorization: tokenHeader } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getProfileMap(profileName, mapId, utmParams) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId;
        var headers = utmParams
          ? { headers: { "X-UTM-Params": utmParams } }
          : null;
        var mapCall = headers ? axios.get(url, headers) : axios.get(url);
        mapCall
          .then(map => {
            resolve(map.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function viewMapStatistics(profileName, mapId, days) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId + "/stats";
      if (days) {
        url = url + "?days=" + days;
      }
      axios
        .get(url, { headers: { Authorization: token } })
        .then(stats => {
          resolve(stats.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getProfileMapPoints(profileName, mapId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(
            apiUrl +
              "/api/profiles/" +
              profileName +
              "/maps/" +
              mapId +
              "/points"
          )
          .then(points => {
            resolve(points.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getPrivateProfileMap(profileName, code, utmParams) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl + "/api/profiles/" + profileName + "/maps/private/" + code;
        var headers = utmParams
          ? { headers: { "X-UTM-Params": utmParams } }
          : null;
        var mapCall = headers ? axios.get(url, headers) : axios.get(url);
        mapCall
          .then(map => {
            resolve(map.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getPrivateProfileMapPoints(profileName, code, utmParams) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/maps/private/" +
          code +
          "/points";
        var headers = utmParams
          ? { headers: { "X-UTM-Params": utmParams } }
          : null;
        var mapCall = headers ? axios.get(url, headers) : axios.get(url);
        mapCall
          .then(points => {
            resolve(points.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileMapForEdit(profileName, mapId) {
    // setGeoHashHeader();
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/maps/all/" + mapId;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(map => {
          resolve(map.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getProfileMapPointsForEdit(profileName, mapId) {
    // setGeoHashHeader();
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/all/" +
        mapId +
        "/points";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(points => {
          resolve(points.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function viewProfileStatistics(profileName, days) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/stats";
      if (days) {
        url = url + "?days=" + days;
      }
      axios
        .get(url, { headers: { Authorization: token } })
        .then(stats => {
          resolve(stats.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /* PROTECTED SERVICES */

  function updateProfile(profileName, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName;
      axios
        .put(url, body, { headers: { Authorization: token } })
        .then(response => {
          var updatedProfile = response.data;
          resolve(updatedProfile);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getAccountInfo(emailAddress) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token.trim();
    }
    return new Promise(function(resolve, reject) {
      let base64 = new Buffer(emailAddress).toString("base64");
      let url = apiUrl + "/api/accounts/" + base64;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updateAccountInfo(emailAddress, body, passwordToken) {
    let token = passwordToken ? "Bearer " + passwordToken : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token.trim();
      }
    }
    return new Promise(function(resolve, reject) {
      let base64 = new Buffer(emailAddress).toString("base64");
      let url = apiUrl + "/api/accounts/" + base64;
      axios
        .put(url, body, { headers: { Authorization: token } })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function uploadFile(profileName, file, exifData, overrideToken) {
    var bodyFormData = new FormData();
    bodyFormData.append("file", file);
    if (exifData) {
      if (exifData.hasOwnProperty("width") && exifData.width) {
        bodyFormData.append("width", exifData.width);
      }
      if (exifData.hasOwnProperty("height") && exifData.height) {
        bodyFormData.append("height", exifData.height);
      }
      if (exifData.hasOwnProperty("createDate") && exifData.createDate) {
        bodyFormData.append("captureDate", exifData.createDate.getTime());
      }
      if (exifData.hasOwnProperty("latitude") && exifData.latitude) {
        bodyFormData.append("latitude", exifData.latitude);
      }
      if (exifData.hasOwnProperty("longitude") && exifData.longitude) {
        bodyFormData.append("longitude", exifData.longitude);
      }
      if (exifData.hasOwnProperty("rotation") && exifData.rotation) {
        bodyFormData.append("rotation", exifData.rotation);
      }
      if (exifData.hasOwnProperty("id") && exifData.id) {
        bodyFormData.append("id", exifData.id);
      }
    }

    var token = overrideToken;
    if (!token) {
      var storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = authentication.token;
      }
    }
    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/files";
      axios
        .post(url, bodyFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: tokenHeader
          }
        })
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function uploadFileAnonymously(file, transactionId, exifData) {
    var bodyFormData = new FormData();
    bodyFormData.append("file", file);
    if (exifData) {
      if (exifData.hasOwnProperty("width") && exifData.width) {
        bodyFormData.append("width", exifData.width);
      }
      if (exifData.hasOwnProperty("height") && exifData.height) {
        bodyFormData.append("height", exifData.height);
      }
      if (exifData.hasOwnProperty("createDate") && exifData.createDate) {
        bodyFormData.append("captureDate", exifData.createDate.getTime());
      }
      if (exifData.hasOwnProperty("latitude") && exifData.latitude) {
        bodyFormData.append("latitude", exifData.latitude);
      }
      if (exifData.hasOwnProperty("longitude") && exifData.longitude) {
        bodyFormData.append("longitude", exifData.longitude);
      }
      if (exifData.hasOwnProperty("rotation") && exifData.rotation) {
        bodyFormData.append("rotation", exifData.rotation);
      }
      if (exifData.hasOwnProperty("id") && exifData.id) {
        bodyFormData.append("id", exifData.id);
      }
    }

    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/files/external/" + transactionId;
        axios
          .post(url, bodyFormData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          })
          .then(response => {
            // TODO get header from response
            resolve(response.data);
          })
          .catch(error => {
            // TODO post something to the user to indicate that something went very wrong
            reject(error);
          });
      });
    });
  }

  function deleteFile(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/files/" + id;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function queryFileGallery(profileName, queryBody) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/gallery/q";
      axios
        .post(url, queryBody, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function retrieveFileGalleryItem(profileName, itemId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/gallery/" + itemId;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function retrieveUnownedFileGalleryItem(itemId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/gallery/" + itemId;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function addFileGalleryItem(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/gallery";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let itemId = parseIdFromLocation(location);
          let result = {
            data: response.data,
            id: itemId
          };
          resolve(result);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updateFileGalleryItem(profileName, itemId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/gallery/" + itemId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function deleteFileGalleryItem(profileName, itemId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/gallery/" + itemId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function forgotPassword(emailAddress, resetMethod = "EMAIL") {
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/users/forgot";
      const body = { emailAddress: emailAddress, resetMethod: resetMethod };
      axios
        .post(url, body)
        .then(response => {
          // TODO get header from response
          let passwordToken = response.headers["x-password-token"];
          cookies.set("passwordToken", passwordToken, {
            secure: config.secureCookies
          });
          resolve(passwordToken);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function forgotPasswordVerify(emailAddress, pin) {
    let token;
    let passwordToken = cookies.get("passwordToken");
    if (passwordToken != null) {
      token = "Bearer " + passwordToken;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/users/forgot/verify";
      const body = { emailAddress: emailAddress, pin: pin };
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          // TODO get header from response
          let accountToken = response.headers["x-account-token"];
          cookies.remove("passwordToken");
          cookies.set("accountToken", accountToken, { secure: config.secureCookies });
          resolve(accountToken);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function resetPassword(emailAddress, password) {
    let token;
    let accountToken = cookies.get("accountToken");
    if (accountToken != null) {
      token = "Bearer " + accountToken;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/users/reset";
      const body = { emailAddress: emailAddress, password: password };
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          let result = { success: true };
          cookies.remove("accountToken");
          resolve(result);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function registerNewProfile(body) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    body["timezone"] = timezone;
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles";
      axios
        .post(url, body)
        .then(response => {
          let location = response.headers["location"];
          let resourceName = parseIdFromLocation(location);
          // TODO get header from response
          let passwordToken = response.headers["x-password-token"];
          let submissionToken = response.headers["x-submission-token"];
          cookies.set("passwordToken", passwordToken, {
            secure: config.secureCookies
          });
          resolve({
            resourceName: resourceName,
            passwordToken: passwordToken,
            submissionToken: submissionToken
          });
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function sendNewProfileAdminInvite(profileName, emailAddress) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/adminRequest";
      const body = { emailAddress: emailAddress };
      axios
        .post(url, body, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let result = { success: true };
          resolve(result);
        })
        .catch(error => {
          let result = { success: false, error: error };
          reject(result);
        });
    });
  }

  function registerNewProfileForAdmin(body, adminRequestToken) {
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + body.resourceName + "/admins";
      axios
        .post(url, body, {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + adminRequestToken
          }
        })
        .then(response => {
          // TODO get header from response
          let passwordToken = response.headers["x-password-token"];
          cookies.set("passwordToken", passwordToken, {
            secure: config.secureCookies
          });
          resolve(passwordToken);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function registerNewCommunityProfile(profileName, body) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    body["timezone"] = timezone;

    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/community/register`;
        axios
          .post(url, body)
          .then(response => {
            let location = response.headers["location"];
            let resourceName = parseIdFromLocation(location);
            // TODO get header from response
            let passwordToken = response.headers["x-password-token"];
            let submissionToken = response.headers["x-submission-token"];
            cookies.set("passwordToken", passwordToken, {
              secure: config.secureCookies
            });
            resolve({
              resourceName: resourceName,
              passwordToken: passwordToken,
              submissionToken: submissionToken
            });
          })
          .catch(error => {
            // TODO post something to the user to indicate that something went very wrong
            reject(error);
          });
      });
    });
  }

  function verifyNewProfile(profileName, emailAddress, pin) {
    let token;
    let passwordToken = cookies.get("passwordToken");
    if (passwordToken != null) {
      token = "Bearer " + passwordToken;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/verify";
      let body = {
        pin: "" + pin,
        email: emailAddress
      };
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          cookies.remove("passwordToken");
          let accessToken = response.headers["x-access-token"];
          // TODO get header from response
          var decoded = jwt_decode(accessToken);
          let authentication = {
            subject: decoded.sub,
            profileName: decoded.profile,
            type: decoded.type,
            scope: decoded.scope,
            expires: decoded.exp,
            token: accessToken
          };
          cookies.set("authentication", JSON.stringify(authentication), {
            expires: new Date(authentication.expires * 1000),
            secure: config.secureCookies,
            path: "/"
          });
          token = accessToken;
          let result = {
            success: true,
            payload: decoded
          };
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + accessToken;
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getPost(profileName, id) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/posts/" + id;
        axios
          .get(url)
          .then(post => {
            resolve(post.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function incrementPostView(profileName, id) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/posts/" + id;
        axios
          .head(url)
          .then(response => {
            resolve({ success: true });
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createPost(profileName, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/posts";
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          let submission = response.headers["x-submission"];
          let location = response.headers["location"];
          let postId = parseIdFromLocation(location);
          let result = {
            submission: submission ? true : null,
            success: true,
            id: postId
          };

          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updatePost(profileName, postId, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/posts/" + postId;
      axios
        .put(url, body, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deletePost(profileName, postId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/posts/" + postId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getPostGallery(profileName, postId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/posts/${postId}/gallery`;
        axios
          .get(url)
          .then(gallery => {
            resolve(gallery.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function addGalleryItemToPost(profileName, postId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/posts/" +
        postId +
        "/gallery";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          var newItem = response.data;
          resolve(newItem);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingGalleryItemInPost(
    profileName,
    postId,
    itemId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/posts/" +
        postId +
        "/gallery/" +
        itemId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          var updatedItem = response.data;
          resolve(updatedItem);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingGalleryItemInPost(profileName, postId, itemId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/posts/" +
        postId +
        "/gallery/" +
        itemId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listMarketplaceCollections(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections`;
        axios
          .get(url)
          .then(collections => {
            resolve(collections.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getMarketplaceCollection(profileName, collectionId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}`;
        axios
          .get(url)
          .then(collection => {
            resolve(collection.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function queryMarketplaceCollection(profileName, collectionId, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/products/q`;
        axios
          .post(url, payload)
          .then(collection => {
            resolve(collection.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createMarketplaceCollection(profileName, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections`;
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let marketplaceId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: marketplaceId,
            data: response.data
          };

          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateMarketplaceCollection(profileName, collectionId, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}`;
      axios
        .put(url, body, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteMarketplaceCollection(profileName, collectionId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}`;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getProduct(profileName, collectionId, id) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/products/${id}`;
        axios
          .get(url)
          .then(product => {
            resolve(product.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function incrementProductView(profileName, collectionId, id) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/products/${id}`;
        axios
          .head(url)
          .then(response => {
            resolve({ success: true });
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createProduct(profileName, collectionId, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/products`;
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let productId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: productId,
            data: response.data
          };

          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function createProductSubmission(profileName, collectionId, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/submissions`;
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let productId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: productId,
            data: response.data
          };

          resolve(result);
        })
        .catch(error => {
          console.log(error);
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function approveProductSubmission(profileName, collectionId, submissionId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/submissions/${submissionId}/approve`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          resolve({
            success: true,
            data: response.data,
            notificationId: notificationId
          });
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function rejectProductSubmission(
    profileName,
    collectionId,
    submissionId,
    reason
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/submissions/${submissionId}/reject`;
      axios
        .post(url, reason, { headers: { Authorization: token } })
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          resolve({ success: true, notificationId: notificationId });
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateProduct(profileName, collectionId, productId, body) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/products/${productId}`;
      axios
        .put(url, body, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteProduct(profileName, collectionId, productId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/products/${productId}`;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listProfileProducts(profileName, collectionId, page, max) {
    // setGeoHashHeader();
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let params = "?";
        if (max) {
          params += "max=" + max;
        }
        if (page) {
          if (params !== "?") {
            params += "&";
          }
          params += "page=" + page;
        }
        if (params === "?") {
          // no params
          params = "";
        }
        axios
          .get(
            `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/products${params}`
          )
          .then(products => {
            resolve(products.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function queryProducts(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .post(apiUrl + "/api/products/q", payload)
          .then(products => {
            resolve(products.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function listProductSubmissions(profileName, collectionId, page, max) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/marketplace/collections/${collectionId}/submissions`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function activationMethod(fragment, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + fragment + "/activate";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function createNewEvent(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/events";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let submission = response.headers["x-submission"];
          let location = response.headers["location"];
          let eventId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: eventId,
            event: response.data,
            submission: submission ? true : null
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingEvent(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/events/" + eventId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            event: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/events/" + eventId;
      axios
        .delete(url, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function event_publishEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/publish";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false
          };
          reject(result);
        });
    });
  }

  function event_unpublishEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/events/" +
        eventId +
        "/unpublish";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false
          };
          reject(result);
        });
    });
  }

  function createNewMap(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/maps";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let mapId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: mapId,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingMap(profileName, mapId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingMap(profileName, mapId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function createNewMapPoint(profileName, mapId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId + "/points";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          // console.log("API createNewMapPoint response", response);
          let location = response.headers["location"];
          let pointId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: pointId
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingMapPoint(profileName, mapId, pointId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/points/" +
        pointId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingMapPoint(profileName, mapId, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/points/" +
        pointId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function queueMapImportJob(profileName, mapId, file) {
    var bodyFormData = new FormData();
    bodyFormData.append("file", file);

    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId + "/imports";
      axios
        .post(url, bodyFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: tokenHeader
          }
        })
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function listMapImportJobs(profileName, mapId) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId + "/imports";
      axios
        .get(url, {
          headers: {
            Authorization: tokenHeader
          }
        })
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function queueMapExportJob(profileName, mapId) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId + "/exports";
      axios
        .post(
          url,
          {},
          {
            headers: {
              Authorization: tokenHeader
            }
          }
        )
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function listMapExportJobs(profileName) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId + "/exports";
      axios
        .get(url, {
          headers: {
            Authorization: tokenHeader
          }
        })
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function downloadMapExportCSV(profileName, mapId, id) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/exports/" +
        id +
        ".csv";
      axios
        .get(url, {
          responseType: "blob",
          headers: { Authorization: token, Accept: "text/csv" }
        })
        .then(csv => {
          resolve(csv.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function downloadMapSampleCSV(profileName, mapId) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "imports/sample.csv";
      axios
        .post(
          url,
          {},
          {
            responseType: "blob",
            headers: { Authorization: token, Accept: "text/csv" }
          }
        )
        .then(csv => {
          resolve(csv.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function listExistingMapRoutes(profileName, mapId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/maps/" +
          mapId +
          "/routes";
        axios
          .get(url)
          .then(routes => {
            resolve(routes.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createNewMapRoute(profileName, mapId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl + "/api/profiles/" + profileName + "/maps/" + mapId + "/routes";
      // console.log("API createNewMapPoint", url, payload);
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          // console.log("API createNewMapPoint response", response);
          let location = response.headers["location"];
          let pointId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: pointId,
            data: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateExistingMapRoute(profileName, mapId, routeId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/routes/" +
        routeId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            data: response.data
          };
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingMapRoute(profileName, mapId, routeId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/maps/" +
        mapId +
        "/routes/" +
        routeId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function queryCommunityMembers(
    profileName,
    type,
    search,
    trusted,
    page,
    max
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let payload = {
        type: type,
        search: search,
        trusted: trusted
      };
      if (max) {
        payload["max"] = max;
      }
      if (page) {
        payload["page"] = page;
      }
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/members/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(members => {
          resolve(members.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function inviteProfileCommunityMember(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/members/invite";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function createSelfManagedMemberProfile(profileName, payload, tokenOverride) {
    let token = tokenOverride ? `Bearer ${tokenOverride}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/members";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updateProfileCommunityMember(
    profileName,
    resourceName,
    payload,
    tokenOverride
  ) {
    let token = tokenOverride
      ? "Bearer " + tokenOverride
      : useDefaultAuthenticationToken();

    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/members/" +
        resourceName;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function removeProfileCommunityMember(profileName, resourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/members/" +
        resourceName;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function hasCommunityAccess(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community";
      axios
        .head(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function sendChildProfileResetEmail(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName +
        "/account_reset";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(result => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function resetChildProfilePassword(
    profileName,
    childProfileName,
    payload,
    tokenOverride
  ) {
    let token = tokenOverride ? "Bearer " + tokenOverride : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName +
        "/reset_password";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(result => {
          console.log("resetChildProfilePassword", result.data);
          var decoded = jwt_decode(result.data.token);
          let authentication = {
            subject: decoded.sub,
            profileName: decoded.profile,
            type: decoded.type,
            scope: decoded.scope,
            expires: decoded.exp,
            token: result.data.token
          };
          cookies.set("authentication", JSON.stringify(authentication), {
            expires: new Date(authentication.expires * 1000),
            secure: config.secureCookies,
            path: "/"
          });
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + result.data.token; // for all requests
          resolve({ success: true, payload: decoded });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function listProfileCommunities(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/my_communities";
        axios
          .post(url, {})
          .then(communities => {
            resolve(communities.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileCommunity(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/community";
        axios
          .get(url)
          .then(community => {
            resolve(community.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileCommunityMember(profileName, childProfileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/community/members/" +
          childProfileName;
        axios
          .get(url)
          .then(communityMember => {
            resolve(communityMember.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getActiveProgramsForCommunityMember(profileName, childProfileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/community/members/" +
          childProfileName +
          "/active_programs";
        axios
          .get(url)
          .then(activePrograms => {
            resolve(activePrograms.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  async function getProfileCommunityWithToken(
    profileName,
    accessToken,
    tokenType
  ) {
    return new Promise(function(resolve, reject) {
      let token = tokenType + " " + accessToken;
      axios
        .get(apiUrl + "/api/profiles/" + profileName + "/community", {
          headers: { Authorization: token }
        })
        .then(community => {
          resolve(community.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getProfileCommunityMap(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/community/map";
        axios
          .get(url)
          .then(map => {
            resolve(map.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileCommunityMapWithToken(
    profileName,
    accessToken,
    tokenType
  ) {
    return new Promise(function(resolve, reject) {
      let token = tokenType + " " + accessToken;
      const url = apiUrl + "/api/profiles/" + profileName + "/community/map";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(map => {
          resolve(map.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updateProfileCommunityMap(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/map";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function queryProfileCommunity(profileName, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/community/q";
        axios
          .post(url, payload)
          .then(result => {
            resolve(result.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function listProfileCommunityMembersByType(
    profileName,
    userProfileType,
    filters
  ) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        var url = `${apiUrl}/api/profiles/${profileName}/community/members/type/${userProfileType}${
          filters ? `?filters=${filters.join(",")}` : ""
        }`;
        axios
          .get(url)
          .then(result => {
            resolve(result.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function queryProfileCommunityMap(profileName, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl + "/api/profiles/" + profileName + "/community/map/q";
        axios
          .post(url, payload)
          .then(result => {
            resolve(result.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function queryProfileCommunityActivity(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/activity";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function pinProfileCommunityActivity(profileName, id, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/activity/pinned/" +
        id;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function unpinProfileCommunityActivity(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/activity/pinned/" +
        id;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function queryProfileCommunityMapWithToken(
    profileName,
    payload,
    accessToken,
    tokenType
  ) {
    return new Promise(function(resolve, reject) {
      let token = tokenType + " " + accessToken;
      const url = apiUrl + "/api/profiles/" + profileName + "/community/map/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updateExistingCommunity(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/community";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(community => {
          resolve(community.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  // ---------------------------------------------------------------------------
  // Forums: a community owns 1..N forums (member portal, partner portal, ...).
  // {profileName} is always the COMMUNITY profile; forums are addressed by id.
  // ---------------------------------------------------------------------------

  function authorizationHeaders() {
    const storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      return { Authorization: "Bearer " + JSON.parse(storedAuth).token };
    }
    return {};
  }

  function forumUrl(profileName, path) {
    return apiUrl + "/api/profiles/" + profileName + "/forums" + (path || "");
  }

  function forumRequest(method, url, data, params) {
    return axios({ method, url, data, params, headers: authorizationHeaders() })
      .then(response => response.data);
  }

  function forum_listForums(profileName) {
    return forumRequest("get", forumUrl(profileName));
  }

  function forum_createForum(profileName, payload) {
    return forumRequest("post", forumUrl(profileName), payload);
  }

  function forum_getForum(profileName, forumId) {
    return forumRequest("get", forumUrl(profileName, "/" + forumId));
  }

  function forum_updateForum(profileName, forumId, payload) {
    return forumRequest("put", forumUrl(profileName, "/" + forumId), payload);
  }

  function forum_deleteForum(profileName, forumId) {
    return forumRequest("delete", forumUrl(profileName, "/" + forumId));
  }

  function forum_updateDigest(profileName, forumId, payload) {
    return forumRequest("put", forumUrl(profileName, "/" + forumId + "/digest"), payload);
  }

  // options: { kind: "ANNOUNCEMENT" | "DISCUSSION", pinned: true | false, page, max }
  function forum_listDiscussions(profileName, forumId, options) {
    const { kind, pinned, page, max } = options || {};
    const params = {};
    if (kind) params.kind = kind;
    if (pinned !== undefined && pinned !== null) params.pinned = pinned;
    if (page) params.page = page;
    if (max) params.max = max;
    return forumRequest("get", forumUrl(profileName, "/" + forumId + "/discussions"), undefined, params);
  }

  function forum_getDiscussion(profileName, forumId, discussionId) {
    return forumRequest("get", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId));
  }

  function forum_createDiscussion(profileName, forumId, payload) {
    return forumRequest("post", forumUrl(profileName, "/" + forumId + "/discussions"), payload);
  }

  function forum_updateDiscussion(profileName, forumId, discussionId, payload) {
    return forumRequest("put", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId), payload);
  }

  function forum_deleteDiscussion(profileName, forumId, discussionId) {
    return forumRequest("delete", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId));
  }

  function forum_pinDiscussion(profileName, forumId, discussionId) {
    return forumRequest("post", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId + "/pin"));
  }

  function forum_unpinDiscussion(profileName, forumId, discussionId) {
    return forumRequest("post", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId + "/unpin"));
  }

  function forum_listReplies(profileName, forumId, discussionId, page, max) {
    const params = {};
    if (page) params.page = page;
    if (max) params.max = max;
    return forumRequest("get", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId + "/replies"), undefined, params);
  }

  function forum_createReply(profileName, forumId, discussionId, payload) {
    return forumRequest("post", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId + "/replies"), payload);
  }

  function forum_updateReply(profileName, forumId, discussionId, replyId, payload) {
    return forumRequest("put", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId + "/replies/" + replyId), payload);
  }

  function forum_deleteReply(profileName, forumId, discussionId, replyId) {
    return forumRequest("delete", forumUrl(profileName, "/" + forumId + "/discussions/" + discussionId + "/replies/" + replyId));
  }

  function forum_listBookmarks(profileName, forumId) {
    return forumRequest("get", forumUrl(profileName, "/" + forumId + "/bookmarks"));
  }

  function forum_addBookmark(profileName, forumId, discussionId) {
    return forumRequest("put", forumUrl(profileName, "/" + forumId + "/bookmarks/" + discussionId));
  }

  function forum_removeBookmark(profileName, forumId, discussionId) {
    return forumRequest("delete", forumUrl(profileName, "/" + forumId + "/bookmarks/" + discussionId));
  }

  function forum_getSubscription(profileName, forumId) {
    return forumRequest("get", forumUrl(profileName, "/" + forumId + "/subscription"));
  }

  function forum_updateSubscription(profileName, forumId, payload) {
    return forumRequest("put", forumUrl(profileName, "/" + forumId + "/subscription"), payload);
  }

  function forum_listUpdates(profileName, forumId, hours, page, max) {
    const params = {};
    if (hours) params.hours = hours;
    if (page) params.page = page;
    if (max) params.max = max;
    return forumRequest("get", forumUrl(profileName, "/" + forumId + "/updates"), undefined, params);
  }

  // ---------------------------------------------------------------------------
  // Portals: one per profile type (ambassador, business, vendor, organization).
  // {profileName} is the COMMUNITY profile; {type} is the lowercased profile type.
  // ---------------------------------------------------------------------------

  function portalUrl(profileName, type, path) {
    return apiUrl + "/api/profiles/" + profileName + "/portals" + (type ? "/" + type : "") + (path || "");
  }

  function portal_listPortals(profileName) {
    return forumRequest("get", portalUrl(profileName));
  }

  function portal_getPortal(profileName, type) {
    return forumRequest("get", portalUrl(profileName, type));
  }

  // payload: { enabled, title, description }
  function portal_updatePortal(profileName, type, payload) {
    return forumRequest("put", portalUrl(profileName, type), payload);
  }

  function portal_getOverview(profileName, type, days) {
    return forumRequest("get", portalUrl(profileName, type, "/overview"), undefined, days ? { days } : undefined);
  }

  function portal_listMembers(profileName, type) {
    return forumRequest("get", portalUrl(profileName, type, "/members"));
  }

  function portal_revokeMember(profileName, type, memberId) {
    return forumRequest("delete", portalUrl(profileName, type, "/members/" + memberId));
  }

  function portal_listInviteCandidates(profileName, type, q) {
    return forumRequest("get", portalUrl(profileName, type, "/invite_candidates"), undefined, q ? { q } : undefined);
  }

  // payload: { contactId, profileId }
  function portal_invite(profileName, type, payload) {
    return forumRequest("post", portalUrl(profileName, type, "/invites"), payload);
  }

  function portal_resendInvite(profileName, type, memberId) {
    return forumRequest("post", portalUrl(profileName, type, "/invites/" + memberId + "/resend"));
  }

  // inviteToken is the delegate token from the emailed link
  function portal_getInvite(profileName, type, memberId, inviteToken) {
    return axios
      .get(portalUrl(profileName, type, "/invites/" + memberId + "/accept"), {
        headers: { Authorization: "Bearer " + inviteToken }
      })
      .then(r => r.data);
  }

  // payload: { givenName, familyName, password } (password only needed for a new account)
  function portal_acceptInvite(profileName, type, memberId, inviteToken, payload) {
    return axios
      .post(portalUrl(profileName, type, "/invites/" + memberId + "/accept"), payload, {
        headers: { Authorization: "Bearer " + inviteToken }
      })
      .then(r => r.data);
  }

  // ---------------------------------------------------------------------------
  // Organizations: contact affiliations, claim invites, partner submissions
  // ---------------------------------------------------------------------------

  // links (or re-links) a contact to a business/organization with an optional { role, title }
  function crm_updateContactAffiliation(profileName, childProfileName, pocId, payload) {
    const url = apiUrl + "/api/profiles/" + profileName + "/crm/profiles/" + childProfileName + "/contacts/" + pocId;
    return axios.put(url, payload || {}, { headers: authorizationHeaders() }).then(r => r.data);
  }

  function crm_listContactAffiliations(profileName, pocId) {
    const url = apiUrl + "/api/profiles/" + profileName + "/crm/contacts/" + pocId + "/affiliations";
    return axios.get(url, { headers: authorizationHeaders() }).then(r => r.data);
  }

  // payload: { contactId, expireAfterDays }
  function crm_sendProfileClaimInvite(profileName, childProfileName, payload) {
    const url = apiUrl + "/api/profiles/" + profileName + "/crm/profiles/" + childProfileName + "/claim_invite";
    return axios.post(url, payload, { headers: authorizationHeaders() }).then(r => r.data);
  }

  // claimToken is the delegate token from the emailed link
  function getProfileClaim(profileName, childProfileName, contactId, claimToken) {
    const url = apiUrl + "/api/profiles/" + profileName + "/community/profiles/" + childProfileName + "/claim";
    return axios
      .get(url, { params: { contact: contactId }, headers: { Authorization: "Bearer " + claimToken } })
      .then(r => r.data);
  }

  // payload: { givenName, familyName, password } (password only needed for a new account)
  function claimProfile(profileName, childProfileName, contactId, claimToken, payload) {
    const url = apiUrl + "/api/profiles/" + profileName + "/community/profiles/" + childProfileName + "/claim";
    return axios
      .post(url, payload, { params: { contact: contactId }, headers: { Authorization: "Bearer " + claimToken } })
      .then(r => r.data);
  }

  // what a community-managed profile (e.g. a partner organization) submitted to its community
  function listOutgoingPointSubmissions(profileName, page, max) {
    const url = apiUrl + "/api/profiles/" + profileName + "/point_submissions/outgoing";
    return axios
      .get(url, { params: { page: page || 0, max: max || 10 }, headers: authorizationHeaders() })
      .then(r => r.data);
  }

  function crm_listUpcomingVolunteerEvents(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/crm/volunteer_program/events/upcoming`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function crm_listPreviousVolunteerEvents(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/crm/volunteer_program/events/previous`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }
  function crm_createNewVolunteerEvent(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/crm/volunteer_program/events`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let eventId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: eventId,
            event: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function crm_updateExistingVolunteerEvent(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/crm/volunteer_program/events/${eventId}`;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function crm_deleteExistingVolunteerEvent(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/crm/volunteer_program/events/${eventId}`;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            event: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function requestCommunityAccess(profileName, resourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/pending/" +
        resourceName;
      axios
        .put(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function approveCommunityAccess(profileName, resourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/pending/" +
        resourceName +
        "/approve";
      axios
        .post(url)
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          resolve({ data: response.data, notificationId: notificationId }); // response.data is the updated community member
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function rejectCommunityAccess(profileName, resourceName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
      axios.defaults.headers.common["Authorization"] =
        "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/pending/" +
        resourceName +
        "/reject";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let notificationId = response.headers["x-notification-id"];
          resolve({ success: true, notificationId: notificationId });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function listPendingProfileCommunityMembers(profileName, page, max) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let params = "?";
      if (max) {
        params += "max=" + max;
      }
      if (page) {
        if (params !== "?") {
          params += "&";
        }
        params += "page=" + page;
      }
      if (params === "?") {
        // no params
        params = "";
      }
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/pending" + params;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(members => {
          resolve(members.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  // function listCommunityManagedProfiles(profileName, page, max) {
  //   let token;
  //   let storedAuth = cookies.get("authentication", { doNotParse: true });
  //   if (storedAuth != null) {
  //     var authentication = JSON.parse(storedAuth);
  //     token = "Bearer " + authentication.token;
  //   }
  //   return new Promise(function(resolve, reject) {
  //     let params = "?";
  //     if (max) {
  //       params += "max=" + max;
  //     }
  //     if (page) {
  //       if (params !== "?") {
  //         params += "&";
  //       }
  //       params += "page=" + page;
  //     }
  //     if (params === "?") {
  //       // no params
  //       params = "";
  //     }
  //     const url =
  //       apiUrl +
  //       "/api/profiles/" +
  //       profileName +
  //       "/community/profiles" +
  //       params;
  //     axios
  //       .get(url, { headers: { Authorization: token } })
  //       .then(members => {
  //         resolve(members.data);
  //       })
  //       .catch(error => {
  //         reject(error);
  //       });
  //   });
  // }

  function getCommunityManagedProfile(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(profile => {
          resolve(profile.data);
        })
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });
  }

  function deleteCommunityManagedProfile(profileName, memberResourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        memberResourceName;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function createCommunityManagedProfile(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/profiles";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updateCommunityManagedProfile(
    profileName,
    childProfileName,
    payload,
    overrideToken
  ) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function submitCommunityManagedProfileTransferRequest(
    profileName,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName +
        "/transferRequest";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(ok => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function transferCommunityManagedProfileToNewOwner(
    profileName,
    childProfileName,
    payload,
    transferToken
  ) {
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName +
        "/transfer";
      axios
        .post(url, payload, {
          headers: { Authorization: "Bearer " + transferToken }
        })
        .then(ok => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function transferCommunityManagedProfileToExistingOwner(
    profileName,
    childProfileName,
    newResourceName,
    payload,
    transferToken
  ) {
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName +
        "/transfer/" +
        newResourceName;
      axios
        .post(url, payload, {
          headers: { Authorization: "Bearer " + transferToken }
        })
        .then(newMember => {
          resolve(newMember.data);
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function delegateCommunityManagedProfileContentRequest(
    profileName,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName +
        "/delegate";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(ok => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function delegateCommunityManagedProfileSubmission(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profile_submissions/delegate";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          var result = {
            success: true,
            token: response.headers["x-delegate-token"]
          };
          resolve(result);
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function submitCommunityManagedProfile(profileName, token, payload) {
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profile_submissions";
      const bearerToken = "Bearer " + token;
      axios
        .post(url, payload, { headers: { Authorization: bearerToken } })
        .then(profile => {
          resolve(profile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function generateApiKey(profileName, apiKeyName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/token/client/" + profileName + "/" + apiKeyName;
      axios
        .post(url, null, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data.token);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteApiKey(profileName, apiKeyName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/token/client/" + profileName + "/" + apiKeyName;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listProfileApiKeys(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/keys";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listProfileAdmins(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = apiUrl + "/api/profiles/" + profileName + "/admins";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function takeAdminProfileAction(profileName, userId, action) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/admins/" +
        userId +
        "/" +
        action;
      axios
        .post(url, null, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function sharePrivateLink(profileName, type, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let urlFragment = "/maps";

      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        urlFragment +
        "/private/" +
        id +
        "/share";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            code: response.headers["x-code"],
            apiUrl: response.headers["location"]
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function unsharePrivateLink(profileName, type, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let urlFragment = "/maps";

      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        urlFragment +
        "/private/" +
        id +
        "/unshare";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getLocationByPlaceId(placeId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/locations/" + placeId;
        axios
          .get(url)
          .then(place => {
            resolve(place.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getLocationsAutocomplete(
    input,
    location,
    business,
    sessionToken = null
  ) {
    var locationStr = location ? location : "";
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/locations/autocomplete?input=${input}${
          location ? `&location=${location}` : ""
        }${business ? `&business=${business}` : ""}${
          sessionToken ? `&t=${sessionToken}` : ""
        }`;
        axios
          .post(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function geoLocationSearch(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/locations";
        axios
          .post(url, payload)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function profileLocationSearch(name, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${name}/profile_locations`;
        axios
          .post(url, payload)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function reverseGeoLocationSearch(latitude, longitude) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/locations/reverse?lat=" +
          latitude +
          "&lon=" +
          longitude;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getCitiesAutocomplete(input) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/cities?input=" + input;
        axios
          .post(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function parseIdFromLocation(url) {
    let parsed = parseURL(url);
    let path = parsed.pathname;
    let splitPath = path.split("/");
    return splitPath[splitPath.length - 1];
  }

  function parseURL(url) {
    var parser = document.createElement("a"),
      searchObject = {},
      queries,
      split,
      i;
    // Let the browser do the work
    parser.href = url;
    // Convert query string to object
    queries = parser.search.replace(/^\?/, "").split("&");
    for (i = 0; i < queries.length; i++) {
      split = queries[i].split("=");
      searchObject[split[0]] = split[1];
    }
    return {
      protocol: parser.protocol,
      host: parser.host,
      hostname: parser.hostname,
      port: parser.port,
      pathname: parser.pathname,
      search: parser.search,
      searchObject: searchObject,
      hash: parser.hash
    };
  }

  function activateUserProfile(profileName, activationToken) {
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/token/" + profileName + "/activate";
      axios
        .post(
          url,
          {},
          { headers: { Authorization: "Bearer " + activationToken } }
        )
        .then(response => {
          var data = response.data;
          var decoded = jwt_decode(data.token);
          let authentication = {
            subject: decoded.sub,
            profileName: decoded.profile,
            type: decoded.type,
            scope: decoded.scope,
            expires: decoded.exp,
            token: data.token
          };
          cookies.set("authentication", JSON.stringify(authentication), {
            expires: new Date(authentication.expires * 1000),
            secure: config.secureCookies,
            path: "/"
          });

          let result = {
            success: true,
            payload: decoded
          };
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + data.token; // for all requests

          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function createAccountForChildProfile(
    profileName,
    childProfileName,
    creationToken
  ) {
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/" +
        childProfileName +
        "/create_account";
      axios
        .post(
          url,
          {},
          { headers: { Authorization: "Bearer " + creationToken } }
        )
        .then(response => {
          var data = response.data;
          var decoded = jwt_decode(data.token);
          let authentication = {
            subject: decoded.sub,
            profileName: decoded.profile,
            type: decoded.type,
            scope: decoded.scope,
            expires: decoded.exp,
            token: data.token
          };
          cookies.set("authentication", JSON.stringify(authentication), {
            expires: new Date(authentication.expires * 1000),
            secure: config.secureCookies,
            path: "/"
          });

          let result = {
            success: true,
            payload: decoded
          };
          axios.defaults.headers.common["Authorization"] =
            "Bearer " + data.token; // for all requests

          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function resendAccountActivationForChildProfile(
    profileName,
    childProfileName,
    activationToken
  ) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/community/profiles/" +
          childProfileName +
          "/resend_account_activation";
        axios
          .post(url, { token: activationToken })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function listProfilePosts(profileName, page, max) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let params = "?";
        if (max) {
          params += "max=" + max;
        }
        if (page) {
          if (params !== "?") {
            params += "&";
          }
          params += "page=" + page;
        }
        if (params === "?") {
          // no params
          params = "";
        }
        axios
          .get(`${apiUrl}/api/profiles/${profileName}/posts${params}`)
          .then(posts => {
            resolve(posts.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfilePostsGeoJson(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/posts.geojson";
      axios
        .get(url, {
          headers: token ? { Authorization: token } : {}
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getProfileMapsGeoJson(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/maps.geojson";
      axios
        .get(url, {
          headers: token ? { Authorization: token } : {}
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function queryPosts(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .post(apiUrl + "/api/posts/q", payload)
          .then(posts => {
            resolve(posts.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getMetadata(host, path) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationTokenFromHost(host, function() {
        const url = host + "/api/profiles" + path + "/metadata";
        axios
          .get(url)
          .then(metadata => {
            resolve(metadata.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getProfileLandingPage(profileName, utmParams) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + profileName + "/landingpage";
        var headers = utmParams
          ? { headers: { "X-UTM-Params": utmParams } }
          : null;
        var landingPageCall = headers
          ? axios.get(url, headers)
          : axios.get(url);
        landingPageCall
          .then(landingPage => {
            resolve({ success: true });
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function updateProfileLandingPage(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/pages/landing";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getUrlMetadata(uri) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let params = "?uri=" + uri;
        const url = apiUrl + "/api/metadata/og" + params;
        axios
          .get(url)
          .then(metadata => {
            resolve(metadata.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getYoutubeMetadata(uri) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let params = "?uri=" + uri;
        const url = apiUrl + "/api/metadata/youtube" + params;
        axios
          .get(url)
          .then(metadata => {
            resolve(metadata.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function lookupDiscoverableContent(code) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let params = "?code=" + code;
        const url = apiUrl + "/api/lookup" + params;
        axios
          .get(url)
          .then(response => {
            const location = response.headers["location"];
            resolve(location);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function logClickRequest(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/clicks";
        axios
          .post(url, payload)
          .then(response => {
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function getRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min); //The maximum is exclusive and the minimum is inclusive
  }

  function submitPointToProfile(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/point_submissions";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getProfilePointSubmissions(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/point_submissions";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(points => {
          resolve(points.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getProfilePointSubmission(profileName, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/point_submissions/" +
        pointId;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(point => {
          resolve(point.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queryProfilePointSubmissions(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/point_submissions/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(points => {
          resolve(points.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function acceptOrRejectProfilePointSubmission(
    profileName,
    pointId,
    accepted,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/point_submissions/" +
        pointId +
        (accepted ? "/approve" : "/reject");
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          // console.log(
          //   "api acceptOrRejectProfilePointSubmission",
          //   response,
          //   response.headers
          // );
          let notificationId = response.headers["x-notification-id"];
          resolve({ success: true, notificationId: notificationId });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queryProfilePoints(name, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + name + "/points/q";
        axios
          .post(url, payload)
          .then(points => {
            resolve(points.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function deleteProfilePoint(profileName, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/points/" + pointId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listCommunityAccolades(name) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/profiles/" + name + "/community/accolades";
        axios
          .get(url)
          .then(accolades => {
            resolve(accolades.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function createCommunityAccolade(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/community/accolades";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(accolade => {
          resolve(accolade.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function editCommunityAccolade(name, id, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/community/accolades/" + id;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(accolade => {
          resolve(accolade.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteCommunityAccolade(name, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/community/accolades/" + id;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function applyCommunityAccoladeToMember(name, memberName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/members/" +
        memberName +
        "/accolades/" +
        id;
      axios
        .put(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function removeCommunityAccoladeFromMember(name, memberName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/members/" +
        memberName +
        "/accolades/" +
        id;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queueCommunityListingImportJob(profileName, file) {
    var bodyFormData = new FormData();
    bodyFormData.append("file", file);

    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/profiles/imports";
      axios
        .post(url, bodyFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: tokenHeader
          }
        })
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function listCommunityListingImportJobs(profileName) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/profiles/imports";
      axios
        .get(url, {
          headers: {
            Authorization: tokenHeader
          }
        })
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function queueCommunityListingExportJob(profileName) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/profiles/exports";
      axios
        .post(
          url,
          {},
          {
            headers: {
              Authorization: tokenHeader
            }
          }
        )
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function listCommunityListingExportJobs(profileName) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = authentication.token;
    }

    var tokenHeader = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/profiles/exports";
      axios
        .get(url, {
          headers: {
            Authorization: tokenHeader
          }
        })
        .then(response => {
          // TODO get header from response
          resolve(response.data);
        })
        .catch(error => {
          // TODO post something to the user to indicate that something went very wrong
          reject(error);
        });
    });
  }

  function downloadCommunityExportCSV(profileName, id) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/exports/" +
        id +
        ".csv";
      axios
        .get(url, {
          responseType: "blob",
          headers: { Authorization: token, Accept: "text/csv" }
        })
        .then(csv => {
          resolve(csv.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function downloadCommunityImportSampleCSV(profileName) {
    var token;
    var storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/profiles/imports/sample.csv";
      axios
        .post(
          url,
          {},
          {
            responseType: "blob",
            headers: { Authorization: token, Accept: "text/csv" }
          }
        )
        .then(csv => {
          resolve(csv.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function listFeatureGroups(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/feature_groups";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(featureGroups => {
          resolve(featureGroups.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSystemLayer(name, type) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/system_layers/" + type;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(layer => {
          resolve(layer.data);
        })
        .catch(error => {
          // 404: the layer has never been imported
          if (error?.response?.status === 404) {
            resolve(null);
          } else {
            reject({ success: false, error });
          }
        });
    });
  }

  function getSystemLayerGeoJson(name, type) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      // the response is cached for 5 minutes; a unique query string reads the latest import
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/system_layers/" +
        type +
        ".geojson?t=" +
        Date.now();
      axios
        .get(url, { headers: { Authorization: token } })
        .then(geojson => {
          resolve(geojson.data);
        })
        .catch(error => {
          if (error?.response?.status === 404) {
            resolve({ type: "FeatureCollection", features: [] });
          } else {
            reject({ success: false, error });
          }
        });
    });
  }

  // public layer types only (e.g. BOUNDARY); accepts an embed API key like crm_publicDataQuery
  function getPublicSystemLayerGeoJson(name, type, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/system_layers/public/" +
        type +
        ".geojson";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(geojson => {
          resolve(geojson.data);
        })
        .catch(error => {
          // 404: the layer has never been imported, or isn't public
          if (error?.response?.status === 404) {
            resolve(null);
          } else {
            reject({ success: false, error });
          }
        });
    });
  }

  function putSystemLayer(name, type, featureCollection, source) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/system_layers/" +
        type +
        (source ? "?source=" + encodeURIComponent(source) : "");
      axios
        .put(url, featureCollection, {
          headers: { Authorization: token, "Content-Type": "application/json" }
        })
        .then(layer => {
          resolve(layer.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listFeatureGroupSummaries(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/feature_groups/summary";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(featureGroups => {
          resolve(featureGroups.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getFeatureGroup(name, featureGroupId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/feature_groups/" + featureGroupId;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(featureGroups => {
          resolve(featureGroups.data?.[0] ?? null);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getFeatureGroups(name, featureGroupIds) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let featureGroupIdUrlPart =
          featureGroupIds && featureGroupIds.length > 0
            ? `/${featureGroupIds.join(",")}`
            : "";
        const url =
          apiUrl +
          "/api/profiles/" +
          name +
          "/feature_groups" +
          featureGroupIdUrlPart;
        axios
          .get(url)
          .then(features => {
            resolve(features.data);
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function getFeatureGroupGeoJson(name, featureGroupId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          name +
          "/feature_groups/" +
          featureGroupId +
          ".geojson";
        axios
          .get(url)
          .then(geojson => {
            resolve(geojson.data);
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function createFeatureGroup(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/feature_groups";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(featureGroup => {
          resolve(featureGroup.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateFeatureGroup(name, id, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/feature_groups/" + id;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(featureGroup => {
          resolve(featureGroup.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteFeatureGroup(name, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/feature_groups/" + id;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(featureGroup => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // adds the features of a GeoJSON FeatureCollection file to an existing feature group
  function importFeatureGroupFeatures(name, featureGroupId, file) {
    var bodyFormData = new FormData();
    bodyFormData.append("file", file);

    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/feature_groups/" +
        featureGroupId +
        "/import";
      axios
        .post(url, bodyFormData, {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data"
          }
        })
        .then(result => {
          resolve(result.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getFeatureGroupFeatures(name, featureIds) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          name +
          "/features" +
          (featureIds
            ? featureIds.map(id => {
                return `/${id}`;
              })
            : "");
        axios
          .get(url)
          .then(features => {
            resolve(features.data);
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function createFeatureGroupFeature(name, id, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/feature_groups/" +
        id +
        "/features";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(feature => {
          resolve(feature.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateFeatureGroupFeature(name, id, featureId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/feature_groups/" +
        id +
        "/features/" +
        featureId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(feature => {
          resolve(feature.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteFeatureGroupFeature(name, id, featureId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/feature_groups/" +
        id +
        "/features/" +
        featureId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // PIN LAYERS

  function listPinLayers(name, overrideToken) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/community/map/pin_layers";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(featureGroups => {
          resolve(featureGroups.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getPublicPinLayers(name, pinLayerIds) {
    return new Promise(function(resolve, reject) {
      let pinLayerUrlPart =
        pinLayerIds && pinLayerIds.length > 0
          ? `/${pinLayerIds.join(",")}`
          : "";
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          name +
          "/community/map/pin_layers/public" +
          pinLayerUrlPart;
        axios
          .get(url)
          .then(pinLayers => {
            resolve(pinLayers.data);
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function getPinLayer(name, pinLayerId, overrideToken) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(pinLayer => {
          resolve(pinLayer.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getPinLayerGeoJson(name, pinLayerId, overrideToken) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        ".geojson";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(geojson => {
          resolve(geojson.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getPinLayerPoints(
    name,
    pinLayerId,
    page = 0,
    max = 1000,
    overrideToken
  ) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/community/map/pin_layers/${pinLayerId}/points?pg=${page}&max=${max}`;

      axios
        .get(url, { headers: { Authorization: token } })
        .then(pinLayer => {
          resolve(pinLayer.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getPinLayerPoint(name, pinLayerId, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/community/map/pin_layers/${pinLayerId}/points/${pointId}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function exportPinLayer(name, pinLayerId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        ".csv";
      axios
        .post(
          url,
          {},
          {
            responseType: "blob",
            headers: { Authorization: token, Accept: "text/csv" }
          }
        )
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function createPinLayer(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/community/map/pin_layers";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(featureGroup => {
          resolve(featureGroup.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updatePinLayer(name, id, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/community/map/pin_layers/" + id;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(featureGroup => {
          resolve(featureGroup.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deletePinLayer(name, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + name + "/community/map/pin_layers/" + id;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(featureGroup => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queuePinLayerImportJob(name, pinLayerId, file) {
    var bodyFormData = new FormData();
    bodyFormData.append("file", file);

    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        "/imports";
      axios
        .post(url, bodyFormData, {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data"
          }
        })
        .then(importJob => {
          resolve(importJob.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listPinLayerImportJobs(name, pinLayerId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        "/imports";
      axios
        .get(url, {
          headers: {
            Authorization: token
          }
        })
        .then(importJobs => {
          resolve(importJobs.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getPinLayerActivityLog(name, id, page = 0, max = 20) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      var url = `${apiUrl}/api/profiles/${name}/community/map/pin_layers/${id}/activity_log?page=${page}&max=${max}`;

      axios
        .get(url, { headers: { Authorization: token } })
        .then(activityLog => {
          resolve(activityLog.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createPinLayerPoint(name, id, payload, overrideToken) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        id +
        "/points";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(newPoint => {
          resolve(newPoint.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updatePinLayerPoint(name, id, pointId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        id +
        "/points/" +
        pointId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(feature => {
          resolve(feature.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function submitPinLayerCTA(name, id, pointId, payload, overrideToken) {
    let token = overrideToken;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = authentication.token;
      }
    }
    let tokenStr = "Bearer " + token;

    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        id +
        "/points/" +
        pointId +
        "/cta";
      axios
        .post(url, payload, { headers: { Authorization: tokenStr } })
        .then(feature => {
          resolve(feature.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deletePinLayerPoint(name, id, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        id +
        "/points/" +
        pointId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // PIN LAYERS DONE

  // ── Pin Layer Routes ──────────────────────────────────────────────────────

  function listPinLayerRoutes(name, pinLayerId, overrideToken) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        "/routes";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function createPinLayerRoute(name, pinLayerId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        "/routes";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let routeId = parseIdFromLocation(location);
          resolve({ success: true, id: routeId, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getPinLayerRoute(name, pinLayerId, routeId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        "/routes/" +
        routeId;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updatePinLayerRoute(name, pinLayerId, routeId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        "/routes/" +
        routeId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deletePinLayerRoute(name, pinLayerId, routeId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/map/pin_layers/" +
        pinLayerId +
        "/routes/" +
        routeId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(() => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // PIN LAYER ROUTES DONE

  function listAssetGroupRoutes(name, overrideToken) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/asset_group_routes";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function createAssetGroupRoute(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/asset_group_routes";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let routeId = parseIdFromLocation(location);
          resolve({ success: true, id: routeId, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getAssetGroupRoute(name, routeId, overrideToken) {
    let token;
    if (overrideToken) {
      token = "Bearer " + overrideToken;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/asset_group_routes/" +
        routeId;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function updateAssetGroupRoute(name, routeId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/asset_group_routes/" +
        routeId;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteAssetGroupRoute(name, routeId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        name +
        "/community/asset_group_routes/" +
        routeId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(() => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // ASSET GROUP ROUTES DONE

  // ─── INCIDENT MANAGEMENT ─────────────────────────────────────────────────

  function _communityBase(name) {
    return `${apiUrl}/api/profiles/${name}/community`;
  }

  function _getAuthHeader() {
    let token;
    const storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      const authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return token;
  }

  function getIncidentTypeRegistry(name, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incident_types/registry`, {
          headers: { Authorization: authHeader }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function listIncidents(name, params, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents`, {
          headers: { Authorization: authHeader },
          params
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getIncident(name, id, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents/${id}`, {
          headers: { Authorization: authHeader }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function createIncident(name, payload, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents`, payload, {
          headers: { Authorization: authHeader }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function updateIncident(name, id, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .put(`${_communityBase(name)}/incidents/${id}`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function deleteIncident(name, id) {
    return new Promise(function(resolve, reject) {
      axios
        .delete(`${_communityBase(name)}/incidents/${id}`, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function resolveIncident(name, id, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/${id}/resolve`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function reopenIncident(name, id, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/${id}/reopen`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function addIncidentComment(name, id, payload, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/${id}/comment`, payload, {
          headers: { Authorization: authHeader }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function assignIncident(name, id, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/${id}/assign`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getIncidentTimeline(name, id, params, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents/${id}/timeline`, {
          headers: { Authorization: authHeader },
          params
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getPendingSubmissions(name, params) {
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents/submissions`, {
          headers: { Authorization: _getAuthHeader() },
          params
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function verifyIncidentSubmission(name, id, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/${id}/verify`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function rejectIncidentSubmission(name, id, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/${id}/reject`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getIncidentTimeseries(name, params, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    console.log("getIncidentTimeseries", name, params);
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents/analytics/timeseries`, {
          headers: { Authorization: authHeader },
          params
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getIncidentHeatmap(name, params, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents/analytics/heatmap`, {
          headers: { Authorization: authHeader },
          params
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getIncidentClusters(name, params, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents/analytics/clusters`, {
          headers: { Authorization: authHeader },
          params
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function generateIncidentShareLink(name, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/analytics/share_link`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function exportIncidentsCsv(name, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/export.csv`, payload, {
          headers: { Authorization: _getAuthHeader() },
          responseType: "blob"
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function exportIncidentsGeoJson(name, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/export.geojson`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function sendIncidentEmailReport(name, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/email_report`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function sweepStaleIncidents(name) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/sweep_stale`, {}, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function requestIncidentUploadToken(name, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/public/upload_token`, payload)
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function uploadIncidentPhoto(name, file, overrideToken, exifData) {
    return new Promise(function(resolve, reject) {
      const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
      const form = new FormData();
      form.append("file", file);
      if (exifData) {
        if (exifData.latitude != null) form.append("latitude", exifData.latitude);
        if (exifData.longitude != null) form.append("longitude", exifData.longitude);
        if (exifData.createDate != null) form.append("captureDate", new Date(exifData.createDate).getTime());
        if (exifData.rotation != null) form.append("rotation", exifData.rotation);
      }
      axios
        .post(`${_communityBase(name)}/incidents/uploads`, form, {
          headers: { Authorization: authHeader }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function uploadPublicIncidentPhoto(name, fileId, file, uploadToken, exifData) {
    return new Promise(function(resolve, reject) {
      const form = new FormData();
      form.append("file", file);
      if (exifData) {
        if (exifData.latitude != null) form.append("latitude", exifData.latitude);
        if (exifData.longitude != null) form.append("longitude", exifData.longitude);
        if (exifData.createDate != null) form.append("captureDate", new Date(exifData.createDate).getTime());
        if (exifData.rotation != null) form.append("rotation", exifData.rotation);
      }
      axios
        .put(`${_communityBase(name)}/incidents/public/uploads/${fileId}`, form,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + uploadToken
            }
          }
        )
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function submitPublicIncident(name, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/public/submit`, payload)
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getPublicIncidentStatus(name, trackingToken) {
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incidents/public/${trackingToken}`)
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  // Community-level incident management configuration

  function generatePublicWorksPortalToken(name) {
    return new Promise(function(resolve, reject) {
      axios
        .post(`${_communityBase(name)}/incidents/portal_token`, {}, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getIncidentManagementConfiguration(name) {
    return new Promise(function(resolve, reject) {
      axios
        .get(`${_communityBase(name)}/incident_management/configuration`, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function updateIncidentManagementConfiguration(name, payload) {
    return new Promise(function(resolve, reject) {
      axios
        .put(`${_communityBase(name)}/incident_management/configuration`, payload, {
          headers: { Authorization: _getAuthHeader() }
        })
        .then(r => resolve(r.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  // Community-level public incident portal (no auth required)

  function getCommunityPubliclySubmittableTypes(name) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(`${_communityBase(name)}/incidents/public/types`)
          .then(r => resolve(r.data))
          .catch(error => reject({ success: false, error }));
      });
    });
  }

  // ─── END INCIDENT MANAGEMENT ──────────────────────────────────────────────

  function listProfileNotifications(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/notifications";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(notifications => {
          resolve(notifications.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function contributeMediaSubmissionToStoryMapPoint(
    name,
    mapId,
    pointId,
    payload,
    overrideToken = null
  ) {
    let token = overrideToken;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = authentication.token;
      }
    }
    let tokenStr = "Bearer " + token;
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/maps/${mapId}/points/${pointId}/media_submissions`;
      axios
        .post(url, payload, { headers: { Authorization: tokenStr } })
        .then(mediaItem => {
          resolve(mediaItem.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listStoryMapPointMediaSubmissions(name, mapId, pointId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/maps/${mapId}/points/${pointId}/media_submissions`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(mediaSubmissions => {
          resolve(mediaSubmissions.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function approveStoryMapPointMediaSubmission(
    name,
    mapId,
    pointId,
    submissionId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/maps/${mapId}/points/${pointId}/media_submissions/${submissionId}/approve`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(updatedMapPoint => {
          let notificationId = updatedMapPoint.headers["x-notification-id"];
          resolve({
            data: updatedMapPoint.data,
            notificationId: notificationId
          });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function rejectStoryMapPointMediaSubmission(
    name,
    mapId,
    pointId,
    submissionId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/maps/${mapId}/points/${pointId}/media_submissions/${submissionId}/reject`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(updatedMapPoint => {
          let notificationId = updatedMapPoint.headers["x-notification-id"];
          resolve({
            data: updatedMapPoint.data,
            notificationId: notificationId
          });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function buildRouteWithDirections(payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/routes/directions`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getOptimizedRoute(payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/routes/optimize`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_uploadFileForStaging(name, file, mappingType) {
    var bodyFormData = new FormData();
    bodyFormData.append("file", file);
    if (mappingType) {
      bodyFormData.append("mappingType", mappingType);
    }

    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/uploads`;
      axios
        .post(url, bodyFormData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_listUploadJobStatus(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/uploads`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_listUploadedJobStatuses(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/uploads`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getUploadJobStatus(name, uploadJobId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/uploads/${uploadJobId}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // Builds the CRM objects an upload describes, with a mapper, for review before
  // publishing. Poll crm_getUploadJobStatus for stagingStatus and the counts.
  function crm_stageUpload(name, uploadJobId, mapperTemplateId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/uploads/${uploadJobId}/stage`;
      axios
        .post(
          url,
          { mapperTemplateId },
          { headers: { Authorization: token } }
        )
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // options: { status, operation, q, page, size }; all optional
  function crm_listStagedRecords(name, uploadJobId, options = {}) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/uploads/${uploadJobId}/staged-records`;
      axios
        .get(url, { headers: { Authorization: token }, params: options })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // Publishes staged records to the CRM in a background job; without ids, every
  // STAGED record of the upload. Poll crm_getPublishedDataImport with the job id.
  function crm_publishStagedRecords(name, uploadJobId, ids) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/staged-records/publish`;
      axios
        .post(url, { uploadId: uploadJobId, ids }, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // Resolves to { discarded, upload } with the upload's refreshed counts.
  function crm_discardStagedRecords(name, uploadJobId, ids) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/staged-records/discard`;
      axios
        .post(url, { uploadId: uploadJobId, ids }, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // Search reindexes run in a background job on the server, which reports nothing when they finish.
  function crm_reindexProfiles(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/profile_reindex`;
      axios
        .post(url, null, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_reindexAddresses(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/address_reindex`;
      axios
        .post(url, null, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_reindexParcels(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/parcel_reindex`;
      axios
        .post(url, null, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_reindexContacts(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/poc_reindex`;
      axios
        .post(url, null, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getStagedRecord(name, stagedRecordId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/staged-records/${stagedRecordId}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getUploadStagedData(name, uploadJobId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/uploads/${uploadJobId}/data`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_listDataMappers(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/mappers`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_listImportableFields(name, mappingType) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/mappers/fields`;
      axios
        .get(url, {
          headers: { Authorization: token },
          params: { type: mappingType }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_createDataMapper(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/mappers`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_updateDataMapper(name, id, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/mappers/${id}`;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_deleteDataMapper(name, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/mappers/${id}`;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listLocatorConfigurations(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/locator_configurations`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getLocatorConfiguration(name, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/locator_configurations/${id}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createLocatorConfiguration(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/locator_configurations`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateLocatorConfiguration(name, id, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/locator_configurations/${id}`;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteLocatorConfiguration(name, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/locator_configurations/${id}`;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_publishDataImport(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/publish`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_listPublishedDataImports(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/publish`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getPublishedDataImport(name, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/publish/${id}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_publicDataQuery(name, queryOptions, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/cms/public_q`;
      axios
        .post(url, queryOptions, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_dataQuery(name, queryOptions) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/q`;
      axios
        .post(url, queryOptions, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getCrmBusinessDashboardView(resourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/crm/businesses/dashboard`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  /**
   * Runs an on-demand Business Inventory Analysis over the tenant's own CRM/PAU data for a
   * requested geofence or point+radius area. Nothing is persisted server-side.
   *
   * @param {string} resourceName
   * @param {object} request — { geoSearch, asOf?, windowStart?, windowEnd?, clusterRadiusMeters? }
   */
  function crm_analyzeBusinessInventory(resourceName, request) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/crm/business-inventory/analyze`;
      axios
        .post(url, request, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getOrganizationGeoJson(profileName) {
    const url = `${apiUrl}/api/profiles/${profileName}/organizations.geojson`;
    return axios
      .get(url, { headers: authorizationHeaders() })
      .then(response => response.data)
      .catch(error => {
        throw { success: false, error };
      });
  }

  function crm_getBusinessGeoJson(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/businesses.geojson`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // ─── DATA EXPLORER ANALYSIS SHARING ─────────────────────────────────────

  function shareDataAnalysis(resourceName, payload) {
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/data-explorer/analysis/share`;
      axios
        .post(url, payload, { headers: { Authorization: _getAuthHeader() } })
        .then(response => resolve(response.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getSharedDataAnalysis(resourceName, id, overrideToken) {
    const authHeader = overrideToken ? "Bearer " + overrideToken : _getAuthHeader();
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/data-explorer/analysis/${id}`;
      axios
        .get(url, { headers: { Authorization: authHeader } })
        .then(response => resolve(response.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function crm_profileExport(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/exports`;
      axios
        .post(url, payload, {
          responseType: "blob",
          headers: { Authorization: token, Accept: "text/csv" }
        })
        .then(csv => {
          resolve(csv.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_profileBulkActions(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/profiles/bulk_actions`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_profileMergeOperation(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/profiles/merge`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_contactExport(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/crm/poc/exports`;
      axios
        .post(url, payload, {
          responseType: "blob",
          headers: { Authorization: token, Accept: "text/csv" }
        })
        .then(csv => {
          resolve(csv.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function listTemplates(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/templates";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queryTemplates(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/templates/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createTemplate(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/templates";
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let location = response.headers["location"];
          let id = parseIdFromLocation(location);
          resolve({ success: true, id: id, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getTemplate(profileName, templateId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/templates/" + templateId;
      axios
        .get(url, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateTemplate(profileName, templateId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/templates/" + templateId;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteTemplate(profileName, templateId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/templates/" + templateId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function copyTemplate(profileName, templateId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/templates/" +
        templateId +
        "/copy";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getCommunityEmailConfiguration(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/email_configuration";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateCommunityEmailConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/email_configuration";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getCommunitySmsConfiguration(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/community/sms_configuration";
        axios
          .get(url)
          .then(response => {
            resolve({ success: true, data: response.data });
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function updateCommunitySmsConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/sms_configuration";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listSentEmails(profileName, page, max, days) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails?days=${days}&page=${page}&max=${max}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSingleEmailPreview(profileName, emailId, previewToken) {
    let token;
    if (previewToken) {
      token = `Bearer ${previewToken}`;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails/${emailId}/preview`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSingleEmailPreviewLink(profileName, emailId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails/${emailId}/preview_link`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          let locationHeader = response.headers["location"];
          resolve({ success: true, url: locationHeader });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSingleEmailStatistics(profileName, emailId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails/${emailId}/stats`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSingleEmailClickStatistics(profileName, emailId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails/${emailId}/clicks`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getEmailEngagementDetails(profileName, emailId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails/${emailId}/details`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function querySentEmails(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails/q`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getEmailEngagementStatistics(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails/stats`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function sendEmail(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/emails`;
      axios
        .post(url, payload, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json"
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function sendSms(profileName, payload, files) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      var bodyFormData = new FormData();
      var input = JSON.stringify(payload);
      bodyFormData.append("input", input);
      if (files) {
        files.forEach(file => {
          bodyFormData.append("files", file);
        });
      }

      const url = `${apiUrl}/api/profiles/${profileName}/sms`;
      axios
        .post(url, bodyFormData, {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data"
          }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getCommunityLandingPageConfiguration(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl + "/api/profiles/" + profileName + "/community/landing_page";
        axios
          .get(url)
          .then(response => {
            resolve({ success: true, data: response.data });
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function updateCommunityLandingPageConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/landing_page";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function disableCommunityLandingPageConfiguration(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/landing_page";
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getCommunityBrandingConfiguration(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl + "/api/profiles/" + profileName + "/community/branding";
        axios
          .get(url)
          .then(response => {
            resolve({ success: true, data: response.data });
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function updateCommunityBrandingConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/branding";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // resolved map pin styles (defaults + community overrides); clientToken for embeds
  function getCommunityMapConfiguration(profileName, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    const url =
      apiUrl + "/api/profiles/" + profileName + "/community/map_configuration";
    const request = () =>
      axios
        .get(url, token ? { headers: { Authorization: token } } : {})
        .then(response => response.data);
    if (token) {
      return request();
    }
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        request()
          .then(resolve)
          .catch(reject);
      });
    });
  }

  function updateCommunityMapConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    const url =
      apiUrl + "/api/profiles/" + profileName + "/community/map_configuration";
    return axios
      .put(url, payload, { headers: { Authorization: token } })
      .then(response => response.data);
  }

  function resetCommunityMapConfiguration(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    const url =
      apiUrl + "/api/profiles/" + profileName + "/community/map_configuration";
    return axios
      .delete(url, { headers: { Authorization: token } })
      .then(response => response.data);
  }

  function getDefaultMapStyles() {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(apiUrl + "/api/map_styles/defaults")
          .then(response => resolve(response.data))
          .catch(reject);
      });
    });
  }

  function deleteCommunityBrandingConfiguration(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/branding";
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getAudienceSubscriptionConfig(profileName, audienceId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .get(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/subscription-config`)
          .then(response => resolve({ success: true, data: response.data }))
          .catch(error => reject({ success: false, error }));
      });
    });
  }

  function updateAudienceSubscriptionConfig(profileName, audienceId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .put(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/subscription-config`, payload, {
          headers: { Authorization: token }
        })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function subscribeToAudience(profileName, audienceId, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        axios
          .post(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/subscribe`, payload)
          .then(response => resolve({ success: true, data: response.data }))
          .catch(error => reject({ success: false, error }));
      });
    });
  }

  function crm_createProfile(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/crm/profiles";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getUsedProfileTags(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/tags";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_getProfile(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_getProfileHealth(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/health";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(health => {
          resolve(health.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_profileAutocomplete(profileName, input, type) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    let payload = {
      input: input,
      userProfileType: type
    };
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/profiles/autocomplete";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_updateProfile(profileName, childProfileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName;
      axios
        .put(url, payload, { headers: { "Content-Type": "application/json", Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_archiveProfile(profileName, childProfileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/archive";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_blockProfileAccess(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/block";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_unblockProfileAccess(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/unblock";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_initiateWorkflow(
    profileName,
    childProfileName,
    workflowType,
    overrideToken
  ) {
    let token = overrideToken ? `Bearer ${overrideToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/workflows/${workflowType}`;
      axios
        .put(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_sendWorkflowResponse(
    profileName,
    childProfileName,
    workflowType,
    payload,
    overrideToken
  ) {
    let token = overrideToken ? `Bearer ${overrideToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/workflows/${workflowType}/response`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getPublicChildProfile(
    profileName,
    childProfileName,
    overrideToken
  ) {
    let token = overrideToken ? `Bearer ${overrideToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/public`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_updatePublicChildProfile(
    profileName,
    childProfileName,
    payload,
    overrideToken
  ) {
    let token = overrideToken ? `Bearer ${overrideToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/public`;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_enhancePublicChildProfile(
    profileName,
    childProfileName,
    gpid,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/public/enhance`;
      const body = { ...(payload ?? {}), ...(gpid ? { gpid } : {}) };
      axios
        .post(url, body, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getUnifiedConfiguration(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/configuration";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(config => {
          resolve(config.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_updateUnifiedConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/configuration";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(config => {
          resolve(config.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_resetWorkflowConfiguration(profileName, workflowType) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/configuration/" +
        workflowType +
        "/reset";
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(config => {
          resolve(config.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_unifiedProfileWorkflowInitiation(
    profileName,
    childProfileName,
    workflowType,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/workflows/${workflowType}/initiation`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(recipient => {
          resolve(recipient.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_listProfilePOC(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/contacts";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(additionalPOC => {
          resolve(additionalPOC.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_getOnboardingStatistics(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/onboarding/stats";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(config => {
          resolve(config.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_queryChildProfileJournalEntry(
    profileName,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/journal/q`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_addChildProfileJournalEntry(
    profileName,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/profiles/${childProfileName}/journal`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_queryContactJournalEntries(profileName, contactId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/contacts/${contactId}/journal/q`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_addContactJournalEntry(profileName, contactId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/contacts/${contactId}/journal`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_getRELocatorGeoJson(profileName, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/cms/locator/re.geojson`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_getREBuildingsGeoJson(profileName, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/cms/locator/re_buildings.geojson`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_reListingQuery(profileName, payload, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/cms/re_listings/q`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_getREListing(profileName, id, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/cms/re_listings/${id}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_getLocatorGeoJson(profileName, mode, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/cms/locator/${mode}.geojson`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getPublicLocatorConfiguration(name, id, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/locator_configurations/${id}/public`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_getLocatorBuildingIndex(name, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/cms/locator/building_index.json`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_getLocatorBuildingProfiles(name, buildingId, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/cms/locator/buildings/${encodeURIComponent(buildingId)}/profiles`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function cms_getChildProfile(profileName, childProfileName, clientToken) {
    let token = clientToken ? `Client ${clientToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/cms/profiles/${childProfileName}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getBusinessContactsFromBusinesses(profileName, businessIds) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/contacts/business_ids";
      axios
        .post(url, { ids: businessIds }, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_getBusinessContactsFromContactIds(profileName, ids) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/contacts/contact_ids";
      axios
        .post(url, { ids: ids }, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_updateContact(profileName, pocId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/contacts/" + pocId;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_linkContactToProfile(profileName, childProfileName, pocId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/contacts/" +
        pocId;
      axios
        .put(url, {}, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve(childProfile.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_setContactAsPrimary(profileName, childProfileName, pocId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/contacts/" +
        pocId +
        "/primary";
      axios
        .put(url, {}, { headers: { Authorization: token } })
        .then(childProfile => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function crm_createContact(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/crm/contacts";
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(newContact => {
          resolve(newContact.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function crm_deleteContact(profileName, pocId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/contacts/" + pocId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  function crm_unlinkContactFromProfile(profileName, childProfileName, pocId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/crm/profiles/" +
        childProfileName +
        "/contacts/" +
        pocId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error: error });
        });
    });
  }

  // TODO check if this is used
  function listCommunityContacts(profileName, page, max) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    let params = "?";
    if (max) {
      params += "max=" + max;
    }
    if (page) {
      if (params !== "?") {
        params += "&";
      }
      params += "page=" + page;
    }
    if (params === "?") {
      // no params
      params = "";
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/contacts${params}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getCommunityContactsTags(name) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${name}/community/contacts/tags`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getCommunityContact(profileName, id, overrideToken) {
    let token = overrideToken ? `Bearer ${overrideToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/crm/contacts/${id}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function communityContactOptIntoSms(profileName, id, payload, overrideToken) {
    let token = overrideToken ? `Bearer ${overrideToken}` : null;
    if (!token) {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/contacts/${id}/sms_optin`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function communityContactAdminOptIntoSms(profileName, id, smsNumber) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/contacts/${id}/sms_admin_optin`;
      axios
        .post(url, { smsNumber }, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function communityContactAdminOptOut(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/contacts/${id}/sms_optout`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function communityContactConsentLog(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/contacts/${id}/sms_consent_log`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function communityContactUpdateBlockedCategories(profileName, id, blockedCategories) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/contacts/${id}/sms_categories`;
      axios
        .put(url, { blockedCategories }, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // ── SMS Provisioning (SU-only) ──────────────────────────────────────────────

  function smsProvisionGetStatus(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/sms/provision/status`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function smsProvisionSync(profileName, syncInfo) {
    return _smsProvisionPost(profileName, "sync", syncInfo);
  }

  function _smsProvisionPost(profileName, step, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/sms/provision/${step}`;
      axios
        .post(url, payload ?? {}, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function smsProvisionCreateSubaccount(profileName) {
    return _smsProvisionPost(profileName, "create-subaccount");
  }

  function smsProvisionCreateApiKey(profileName) {
    return _smsProvisionPost(profileName, "create-api-key");
  }

  function smsProvisionCreateCustomerProfile(profileName, provisioningInfo) {
    return _smsProvisionPost(profileName, "create-customer-profile", provisioningInfo);
  }

  function smsProvisionCreateTrustProduct(profileName, provisioningInfo) {
    return _smsProvisionPost(profileName, "create-trust-product", provisioningInfo);
  }

  function smsProvisionSubmitBrandRegistration(profileName) {
    return _smsProvisionPost(profileName, "submit-brand-registration");
  }

  function smsProvisionCreateMessagingService(profileName) {
    return _smsProvisionPost(profileName, "create-messaging-service");
  }

  function smsProvisionPurchaseNumber(profileName, provisioningInfo) {
    return _smsProvisionPost(profileName, "purchase-number", provisioningInfo ?? {});
  }

  function smsProvisionCreateCampaign(profileName) {
    return _smsProvisionPost(profileName, "create-campaign");
  }

  function smsProvisionActivate(profileName) {
    return _smsProvisionPost(profileName, "activate");
  }

  /**
   * Start (or resume) self-service provisioning.  Runs all executable steps and
   * returns SmsProvisioningStatusReport (per-step detail + overall status).
   *
   * @param {string} profileName
   * @param {object} provisioningInfo  TenantProvisioningInfo payload
   */
  function smsProvisionStart(profileName, provisioningInfo) {
    return _smsProvisionPost(profileName, "start", provisioningInfo);
  }

  // ── SMS Send History ────────────────────────────────────────────────────────

  function listSentSms(profileName, page, max, days) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/sms?days=${days}&page=${page}&max=${max}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function querySentSms(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/sms/q`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSingleSmsStatistics(profileName, smsId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/sms/${smsId}/stats`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSmsEngagementDetails(profileName, smsId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/sms/${smsId}/details`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function communityUnsubscribeFromEmails(profileName, base64, overrideToken) {
    let token = overrideToken ? `Bearer ${overrideToken}` : null;
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/unsubscribe/${base64}`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queryCommunityContacts(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/contacts/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function resolveCommunityContactsByTag(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/contacts/tagged";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function lookupCommunityContacts(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/contacts/lookup";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function communityContactBulkActions(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/contacts/bulk_actions";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createCommunityContact(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/crm/contacts";
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let location = response.headers["location"];
          let id = parseIdFromLocation(location);
          resolve({ success: true, id: id, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateCommunityContact(profileName, contactId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/contacts/" + contactId;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteCommunityContact(profileName, contactId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/crm/contacts/" + contactId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function initiateContactVerification(profileName, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/community/contacts/verify";
        axios
          .post(url, payload, {
            headers: { "Content-Type": "application/json" }
          })
          .then(response => {
            let token = response.headers["x-verify-token"];
            let id = response.headers["x-verify-id"];
            resolve({ success: true, verificationToken: token, contactId: id });
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function contactVerification(profileName, contactId, payload, overrideToken) {
    let token = "Bearer " + overrideToken;
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/contacts/verify/" +
        contactId;
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let token = response.headers["x-delegate-token"];
          resolve({ success: true, token: token, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listManagedEvents(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listNextManagedEvents(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/upcoming`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listPreviousManagedEvents(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/previous`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }
  function createNewManagedEvent(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let location = response.headers["location"];
          let eventId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: eventId,
            event: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function copyExistingManagedEvent(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${id}/copy`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getExistingManagedEvent(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}`;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function updateExistingManagedEvent(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}`;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            event: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteExistingManagedEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}`;
      axios
        .delete(url, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function archiveExistingManagedEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/archive`;
      axios
        .post(
          url,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: token
            }
          }
        )
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listManagedEventParticipants(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/participants`;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function listManagedEventSchedule(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/schedule`;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function getManagedEventScheduledEvent(profileName, eventId, subEventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/schedule/${subEventId}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function addEventToManagedEventSchedule(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/schedule`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let submission = response.headers["x-submission"];
          let location = response.headers["location"];
          let eventId = parseIdFromLocation(location);
          let result = {
            success: true,
            id: eventId,
            event: response.data,
            submission: submission ? true : null
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateEventOnManagedEventSchedule(
    profileName,
    eventId,
    subEventId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/schedule/${subEventId}`;
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true,
            event: response.data
          };
          resolve(result);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function removeEventFromManagedEventSchedule(
    profileName,
    eventId,
    subEventId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/schedule/${subEventId}`;
      axios
        .delete(url, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getManagedEventParticipantGroupParticipants(
    profileName,
    eventId,
    groupType
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${groupType}/participants`;
        axios
          .get(url, { headers: { Authorization: token } })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function eventParticipantGroupParticipantBulkActions(
    profileName,
    eventId,
    groupType,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${groupType}/participants/bulk_actions`;
        axios
          .post(url, payload, { headers: { Authorization: token } })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function exportManagedEventGroupParticipants(
    profileName,
    eventId,
    groupType
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${groupType}/participants.csv`;
      axios
        .post(
          url,
          {},
          {
            responseType: "blob",
            headers: { Authorization: token, Accept: "text/csv" }
          }
        )
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(error);
        });
    });
  }

  function exportManagedEventGroupSubmissions(profileName, eventId, groupType) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${groupType}/submissions.csv`;
      axios
        .post(
          url,
          {},
          {
            responseType: "blob",
            headers: { Authorization: token, Accept: "text/csv" }
          }
        )
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(error);
        });
    });
  }

  function getManagedEventParticipantGroups(profileName, eventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups`;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function getManagedEventParticipantGroupConfiguration(
    profileName,
    eventId,
    type
  ) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${type}`;
        axios
          .get(url)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function updateManagedEventParticipantGroupConfiguration(
    profileName,
    eventId,
    type,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${type}`;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getSingleBookingPaymentDetails(
    profileName,
    id,
    childProfileName,
    sessionId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${id}/market/bookings/${childProfileName}/payments/${sessionId}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getBookingPaymentDetailsForProfile(
    profileName,
    id,
    childProfileName,
    sessionId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${id}/market/bookings/${childProfileName}/payments/`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getVendorPayments(profileName, vendorProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/vendors/${vendorProfileName}/payments`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getMarketPayments(profileName, eventId, page, max) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/market/payments?${
        page ? "page=" + page : ""
      }${max ? "&max=" + max : ""}`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getProducts(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/products`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_createCreditProduct(profileName, productData) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/products`;
      axios
        .post(url, productData, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_updateCreditProduct(profileName, productId, productData) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/products/${productId}`;
      axios
        .put(url, productData, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_deleteCreditProduct(profileName, productId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/products/${productId}`;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          let result = {
            success: true
          };
          resolve(result);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  async function market_getCreditPaymentIntent(
    profileName,
    childProfileName,
    productId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/credits/${childProfileName}/payment_intent/${productId}`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(paymentIntent => {
          resolve(paymentIntent.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getVendorPaymentDetailsForSession(
    profileName,
    childProfileName,
    sessionId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/vendors/${childProfileName}/payments/${sessionId}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_queryMarketPayments(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/payments/q`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function createManagedEventParticipantGroupParticipant(
    profileName,
    eventId,
    type,
    childResourceName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${type}/participants${
        childResourceName ? "/" + childResourceName : ""
      }`;
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(eventParticipant => {
          resolve(eventParticipant.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function updateManagedEventParticipantGroupParticipant(
    profileName,
    eventId,
    type,
    participantId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${type}/participants/${participantId}`;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(eventParticipant => {
          resolve(eventParticipant.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function deleteManagedEventParticipantGroupParticipant(
    profileName,
    eventId,
    type,
    participantId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${type}/participants/${participantId}`;
      axios
        .delete(url, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(result => {
          resolve({
            success: true
          });
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function managedEventParticipantSubmissionWithPOC(
    profileName,
    eventId,
    type,
    pocId,
    payload,
    overrideToken
  ) {
    let token = `Bearer ${overrideToken}`;
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${type}/submissions/${pocId}`;
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function managedEventParticipantSubmissionNoPOC(
    profileName,
    eventId,
    type,
    payload
  ) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${type}/submissions`;
        axios
          .post(url, payload)
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function getManagedEventParticipantGroupSubmissions(
    profileName,
    eventId,
    groupType
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${groupType}/submissions`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function approveEventParticipantGroupSubmission(
    profileName,
    eventId,
    groupType,
    applicantId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${groupType}/submissions/${applicantId}/approve`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function rejectEventParticipantGroupSubmission(
    profileName,
    eventId,
    groupType,
    applicantId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/groups/${groupType}/submissions/${applicantId}/reject`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function getVendorScheduleConfiguration(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/market/schedule_configuration`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getNextMarketEvent(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/market/events/next`;
        axios
          .get(url)
          .then(response => {
            let location = response.headers["location"];
            resolve({ url: location });
          })
          .catch(error => {
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function market_getMarketConfiguration(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/configuration`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getMarketResources(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/resources`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(resources => {
          resolve(resources.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_updateMarketConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/configuration`;
      axios
        .put(url, payload, {
          headers: { Authorization: token }
        })
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getMarketApplication(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/market/workflows/vendorApplication`;
        axios
          .get(url)
          .then(application => {
            resolve(application.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function market_getExistingMarketApplication(
    profileName,
    vendorProfileName,
    overrideToken
  ) {
    let token;
    if (overrideToken) {
      token = `Bearer ${overrideToken}`;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/workflows/vendorApplication/${vendorProfileName}`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(application => {
          resolve(application.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_submitMarketApplication(profileName, payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/market/workflows/vendorApplication`;
        axios
          .post(url, payload)
          .then(application => {
            resolve(application.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function market_resubmitMarketApplication(
    profileName,
    vendorProfileName,
    payload,
    overrideToken
  ) {
    let token;
    if (overrideToken) {
      token = `Bearer ${overrideToken}`;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/workflows/vendorApplication/${vendorProfileName}`;
      axios
        .put(url, payload, {
          headers: { Authorization: token }
        })
        .then(application => {
          resolve(application.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_sendActivationEmail(profileName, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/applicants/${childProfileName}/approve/email`;
      axios
        .post(
          url,
          {},
          {
            headers: { Authorization: token }
          }
        )
        .then(vendor => {
          resolve(vendor.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_approveMarketApplication(
    profileName,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/applicants/${childProfileName}/approve`;
      axios
        .post(url, payload, {
          headers: { Authorization: token }
        })
        .then(vendor => {
          resolve(vendor.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_rejectMarketApplication(
    profileName,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/applicants/${childProfileName}/reject`;
      axios
        .post(url, payload, {
          headers: { Authorization: token }
        })
        .then(vendor => {
          resolve(vendor.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_refuseMarketApplication(
    profileName,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/applicants/${childProfileName}/refuse`;
      axios
        .post(url, payload, {
          headers: { Authorization: token }
        })
        .then(vendor => {
          resolve(vendor.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getMarketVendorsOfType(profileName, status) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/vendors/${status}`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(vendors => {
          resolve(vendors.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getMarketEventConfiguration(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/market/configuration`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_updateMarketEventConfiguration(
    profileName,
    eventId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/market/configuration`;
      axios
        .put(url, payload, {
          headers: { Authorization: token }
        })
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_publishMarketEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/market/publish`;
      axios
        .post(
          url,
          {},
          {
            headers: { Authorization: token }
          }
        )
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_unpublishMarketEvent(profileName, eventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/market/unpublish`;
      axios
        .post(
          url,
          {},
          {
            headers: { Authorization: token }
          }
        )
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getVendorSchedule(profileName, managedEventId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(schedule => {
          resolve(schedule.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getVendorScheduleMap(profileName, managedEventId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/map`;
        axios
          .get(url)
          .then(schedule => {
            resolve(schedule.data);
          })
          .catch(error => {
            // pin was incorrect or something else went wrong
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function market_getVendorScheduleForDate(
    profileName,
    managedEventId,
    datestamp
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      datestamp = datestamp.replace(/\//g, "-");

      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(schedule => {
          resolve(schedule.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getVendorScheduleMapForDate(
    profileName,
    managedEventId,
    datestamp
  ) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        datestamp = datestamp.replace(/\//g, "-");
        let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}.geojson`;
        axios
          .get(url)
          .then(markets => {
            resolve(markets.data);
          })
          .catch(error => {
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function market_getSingleVendorSchedule(
    profileName,
    managedEventId,
    participantId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/for/${participantId}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(schedule => {
          resolve(schedule.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_exportVendorScheduleForDate(
    profileName,
    managedEventId,
    datestamp
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      datestamp = datestamp.replace(/\//g, "-");
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}.csv`;
      axios
        .post(
          url,
          {},
          {
            responseType: "blob",
            headers: { Authorization: token, Accept: "text/csv" }
          }
        )
        .then(schedule => {
          resolve(schedule.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getAvailableVendorsForDate(
    profileName,
    managedEventId,
    datestamp
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      // replace forward slashes in the datestamp with dashes for the API request
      datestamp = datestamp.replace(/\//g, "-");

      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/vendors`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(vendors => {
          resolve(vendors.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_addVendorToSchedule(
    profileName,
    managedEventId,
    datestamp,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      // replace forward slashes in the datestamp with dashes for the API request
      datestamp = datestamp.replace(/\//g, "-");

      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/vendors`;
      axios
        .post(url, payload, {
          headers: { Authorization: token }
        })
        .then(vendors => {
          resolve(vendors.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_putVendorOnVendorSchedule(
    profileName,
    managedEventId,
    datestamp,
    spacePlanId,
    spaceId,
    participantUserProfileId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      // replace forward slashes in the datestamp with dashes for the API request
      datestamp = datestamp.replace(/\//g, "-");

      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/${spacePlanId}/${spaceId}/${participantUserProfileId}`;
      axios
        .put(
          url,
          {},
          {
            headers: { Authorization: token }
          }
        )
        .then(space => {
          resolve(space.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_addVendorToVendorSchedule(
    profileName,
    managedEventId,
    datestamp,
    spacePlanId,
    spaceId,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      // replace forward slashes in the datestamp with dashes for the API request
      datestamp = datestamp.replace(/\//g, "-");

      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/${spacePlanId}/${spaceId}`;
      axios
        .post(url, payload, {
          headers: { Authorization: token }
        })
        .then(space => {
          resolve(space.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_removeVendorFromVendorSchedule(
    profileName,
    managedEventId,
    datestamp,
    spacePlanId,
    spaceId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      datestamp = datestamp.replace(/\//g, "-");
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/${spacePlanId}/${spaceId}`;
      axios
        .delete(url, {
          headers: { Authorization: token }
        })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_removeVendorFromScheduledDate(
    profileName,
    managedEventId,
    datestamp,
    vendorResourceName
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      // replace forward slashes in the datestamp with dashes for the API request
      datestamp = datestamp.replace(/\//g, "-");

      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/vendors/${vendorResourceName}`;
      axios
        .delete(url, {
          headers: { Authorization: token }
        })
        .then(() => {
          resolve({ success: true });
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_markVendorScheduleSpaceAsUnavailable(
    profileName,
    managedEventId,
    datestamp,
    spacePlanId,
    spaceId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      datestamp = datestamp.replace(/\//g, "-");
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/${spacePlanId}/${spaceId}/UNAVAILABLE`;
      axios
        .post(
          url,
          {},
          {
            headers: { Authorization: token }
          }
        )
        .then(space => {
          resolve(space.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_publishMarketEventSchedule(profileName, eventId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${eventId}/market/publish_schedule`;
      axios
        .post(url, payload, {
          headers: { Authorization: token }
        })
        .then(response => {
          resolve(response.data); // map of updated schedules based on datestamp
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_sendManagedEventMarketEmailSequence(
    profileName,
    managedEventId,
    datestamp,
    sequenceType,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      datestamp = datestamp.replace(/\//g, "-");
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${managedEventId}/market/schedule/${datestamp}/email_sequences/${sequenceType}`;
      axios
        .post(url, payload, {
          headers: { Authorization: token }
        })
        .then(space => {
          resolve(space.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_retrieveMyUpcomingMarketEvents(
    profileName,
    childProfileName
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/events/mine/${childProfileName}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(markets => {
          resolve(markets.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getUpcomingMarketEvents(profileName) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        let url = `${apiUrl}/api/profiles/${profileName}/market/events`;
        axios
          .get(url)
          .then(markets => {
            resolve(markets.data);
          })
          .catch(error => {
            let result = {
              success: false,
              error: error
            };
            reject(result);
          });
      });
    });
  }

  function market_getAllMarketEvents(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/events/all`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(markets => {
          resolve(markets.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_getUpcomingMarketEventsForVendor(
    profileName,
    childProfileName
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/market/events/for/${childProfileName}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(markets => {
          resolve(markets.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function market_cancelMarketEventReservation(
    profileName,
    id,
    childProfileName,
    paymentTransactionId,
    payload
  ) {
    // console.log("market_cancelMarketEventReservation", profileName, id, childProfileName, paymentTransactionId, payload);
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${id}/market/bookings/${childProfileName}/payments/${paymentTransactionId}/cancel`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function market_getBookingConfiguration(profileName, id, childProfileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${id}/market/bookings/${childProfileName}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(configuration => {
          resolve(configuration.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  async function market_getPaymentIntent(
    profileName,
    id,
    childProfileName,
    payload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/managed_events/${id}/market/bookings/${childProfileName}/payment_intent`;
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(paymentIntent => {
          resolve(paymentIntent.data);
        })
        .catch(error => {
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listProfileSpacePlans(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/space_plans`;
      axios
        .get(url, {
          headers: { Authorization: token }
        })
        .then(spacePlans => {
          resolve(spacePlans.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(result);
        });
    });
  }

  function listTemplateForms() {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/template_forms";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createFormFromTemplate(profileName, templateResourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/forms/from/" +
        templateResourceName;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listForms(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/forms";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queryForms(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/forms/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function formsAutocomplete(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/forms/autocomplete";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createForm(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/forms";
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let location = response.headers["location"];
          let id = parseIdFromLocation(location);
          resolve({ success: true, id: id, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getForm(profileName, formId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/forms/" + formId;
      axios
        .get(url, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateForm(profileName, formId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/forms/" + formId;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function copyForm(profileName, formId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/forms/" + formId + "/copy";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteForm(profileName, formId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/forms/" + formId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queryIcons(payload) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = apiUrl + "/api/icons/q";
        axios
          .post(url, payload)
          .then(predictions => {
            resolve(predictions.data);
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function listTemplateSurveys() {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/template_surveys";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createSurveyFromTemplate(profileName, templateResourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/from/" +
        templateResourceName;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listSurveys(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/surveys";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function querySurveys(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/surveys/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function surveysAutocomplete(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/autocomplete";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createSurvey(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/surveys";
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          let location = response.headers["location"];
          let id = parseIdFromLocation(location);
          resolve({ success: true, id: id, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSurvey(profileName, surveyId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url =
          apiUrl +
          "/api/profiles/" +
          profileName +
          "/community/surveys/" +
          surveyId;
        axios
          .get(url, {
            headers: { "Content-Type": "application/json" }
          })
          .then(response => {
            resolve(response.data);
          })
          .catch(error => {
            reject({ success: false, error });
          });
      });
    });
  }

  function updateSurvey(profileName, surveyId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/" +
        surveyId;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function copySurvey(profileName, surveyId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/" +
        surveyId +
        "/copy";
      axios
        .post(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          console.log(error);
          reject({ success: false, error: error });
        });
    });
  }

  function deleteSurvey(profileName, surveyId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/" +
        surveyId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function startPublicSurvey(profileName, surveyId) {
    return new Promise(function(resolve, reject) {
      getAnonymousAuthenticationToken(() => {
        const url = `${apiUrl}/api/profiles/${profileName}/community/surveys/${surveyId}/collection/public_submissions`;
        axios
          .post(url, {})
          .then(response => {
            let surveyToken = response.headers["x-survey-token"];
            resolve({ data: response.data, token: surveyToken });
          })
          .catch(error => {
            reject(error);
          });
      });
    });
  }

  function submitPublicSurveyResponse(
    profileName,
    surveyId,
    submissionId,
    payload,
    surveyToken
  ) {
    let token = `Bearer ${surveyToken}`;

    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/community/surveys/${surveyId}/collection/public_submissions/${submissionId}`;

      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function startPrivateSurvey(
    profileName,
    surveyId,
    participantId,
    overrideToken
  ) {
    let token;
    if (overrideToken) {
      token = `Bearer ${overrideToken}`;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/surveys/${surveyId}/collection/private_submissions/${participantId}`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          // let surveyToken = response.headers["x-survey-token"];
          resolve({ data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function submitPrivateSurveyResponse(
    profileName,
    surveyId,
    participantId,
    payload,
    overrideToken
  ) {
    let token;
    if (overrideToken) {
      token = `Bearer ${overrideToken}`;
    } else {
      let storedAuth = cookies.get("authentication", { doNotParse: true });
      if (storedAuth != null) {
        var authentication = JSON.parse(storedAuth);
        token = "Bearer " + authentication.token;
      }
    }

    return new Promise(function(resolve, reject) {
      let url = `${apiUrl}/api/profiles/${profileName}/community/surveys/${surveyId}/collection/private_submissions/${participantId}`;

      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listSurveyHistory(profileName, page, max) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/community/survey_history";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getSingleSurveyHistory(profileName, surveyId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/" +
        surveyId +
        "/history";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listSurveyResponses(profileName, surveyId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/" +
        surveyId +
        "/responses";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteSurveyResponse(profileName, surveyId, submissionId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/" +
        surveyId +
        "/responses/" +
        submissionId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function exportSurveyResponses(profileName, surveyId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl +
        "/api/profiles/" +
        profileName +
        "/community/surveys/" +
        surveyId +
        "/responses.csv";
      axios
        .post(
          url,
          {},
          {
            responseType: "blob",
            headers: { Authorization: token, Accept: "text/csv" }
          }
        )
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          // pin was incorrect or something else went wrong
          let result = {
            success: false,
            error: error
          };
          reject(error);
        });
    });
  }

  function listDocuments(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/documents";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function queryDocuments(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/documents/q";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function createDocument(profileName, payload, file) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      var bodyFormData = new FormData();
      var input = JSON.stringify(payload);
      bodyFormData.append("input", input);
      if (file) {
        bodyFormData.append("file", file);
      }

      const url = `${apiUrl}/api/profiles/${profileName}/documents`;
      axios
        .post(url, bodyFormData, {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data"
          }
        })
        .then(response => {
          console.log(response);
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function getDocuments(profileName, documentIds) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      let documentIdString = documentIds.join(",");
      const url = `${apiUrl}/api/profiles/${profileName}/documents/${documentIdString}`;
      axios
        .get(url, {
          headers: { Authorization: token, "Content-Type": "application/json" }
        })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateDocument(profileName, documentId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/documents/" + documentId;
      axios
        .put(url, payload, {
          headers: { "Content-Type": "application/json", Authorization: token }
        })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function deleteDocument(profileName, documentId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/documents/" + documentId;
      axios
        .delete(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function listTemplateEvents(profileName, type) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/template_events/${type}`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function createEventFromTemplate(profileName, resourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/template_events/from/${resourceName}`;
      axios
        .post(url, {}, { headers: { Authorization: token } })
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function eventsAutocomplete(name, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + name + "/events/autocomplete";
      axios
        .post(url, payload, { headers: { Authorization: token } })
        .then(predictions => {
          resolve(predictions.data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  function getVolunteerManagementConfiguration(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/volunteer_management";
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function updateVolunteerManagementConfiguration(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url =
        apiUrl + "/api/profiles/" + profileName + "/volunteer_management";
      axios
        .put(url, payload, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  // ── DB Connectors ──────────────────────────────────────────────────────────

  function listDbConnectors(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/db-connectors`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getDbConnector(profileName, connectorId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/db-connectors/${connectorId}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function createDbConnector(profileName, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/db-connectors`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function updateDbConnector(profileName, connectorId, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .put(`${apiUrl}/api/profiles/${profileName}/db-connectors/${connectorId}`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function deleteDbConnector(profileName, connectorId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .delete(`${apiUrl}/api/profiles/${profileName}/db-connectors/${connectorId}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  /**
   * Mint a presigned URL for a connector's parquet file.
   * Works for both protected (returns presigned URL + expiresAt) and unprotected
   * (returns canonical URL + null expiresAt) connectors — one code path for the client.
   */
  function getDbConnectorAccessUrl(profileName, connectorId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/db-connectors/${connectorId}/access-url`, {}, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  // ── End DB Connectors ──────────────────────────────────────────────────────

  // ── Overture Maps ──────────────────────────────────────────────────────────

  /**
   * Returns the list of supported Overture Maps dataset types with metadata
   * and default query templates.
   */
  function getOvertureCatalog() {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/overture/catalog`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  /**
   * Returns the HTTPS parquet file URLs for a given Overture type that
   * intersect the provided geographic bounding box.
   *
   * @param {string} theme    - Overture theme (e.g. "places")
   * @param {string} type     - Overture type  (e.g. "place")
   * @param {object} bbox     - { west, south, east, north } in EPSG:4326 degrees
   */
  function getOvertureFiles(theme, type, bbox) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    const params = {
      theme,
      type,
      minLon: bbox?.west  ?? -180,
      minLat: bbox?.south ?? -90,
      maxLon: bbox?.east  ??  180,
      maxLat: bbox?.north ??   90,
    };
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/overture/files`, { headers: { Authorization: token }, params })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  /**
   * Imports a single Overture place/POI GeoJSON Feature (e.g. one selected on the Data Explorer map) into a
   * child profile of profileName. Rejects with { success: false, code: 409 } if this place was already
   * imported (matched server-side on ownerId + externalId + source).
   *
   * @param {string} profileName
   * @param {object} feature - GeoJSON Feature, as selected from the Overture places map layer
   */
  function importOverturePlace(profileName, feature) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/overture/places`, feature, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, code: error.response?.status, error }));
    });
  }

  // ── End Overture Maps ──────────────────────────────────────────────────────

  // ── Isochrone Analysis ─────────────────────────────────────────────────────

  /**
   * Generates walking, cycling, and driving isochrones (5/10/15-min contours)
   * for a given lat/lon. Returns a transient response — nothing is stored server-side.
   *
   * @param {number} latitude
   * @param {number} longitude
   * @returns {{ isochronesWalking, isochronesCycling, isochronesDriving }}
   */
  function analyzeIsochrones(latitude, longitude) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/isochrone/analyze`, { latitude, longitude }, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  // ── End Isochrone Analysis ─────────────────────────────────────────────────

  // ── STAC Dataset Connector ─────────────────────────────────────────────────

  /**
   * Returns proxy-URL objects for parquet files in the given STAC datasource
   * whose bounding boxes intersect the provided geographic bbox.
   *
   * @param {string} datasourceId  — e.g. "acs24" or "lodes23"
   * @param {{ west, south, east, north }} bbox  — in EPSG:4326 degrees
   * @returns {Array<{ key: string, url: string }>}
   */
  function getDatasetFiles(datasourceId, bbox) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    const params = {
      minLon: bbox?.west  ?? -180,
      minLat: bbox?.south ?? -90,
      maxLon: bbox?.east  ??  180,
      maxLat: bbox?.north ??   90,
    };
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/datasets/${datasourceId}/files`, { headers: { Authorization: token }, params })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  // ── End STAC Dataset Connector ─────────────────────────────────────────────

  // ── Campaigns ──────────────────────────────────────────────────────────────

  function listCampaigns(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/campaigns`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getCampaign(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function createCampaign(profileName, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function updateCampaign(profileName, id, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .put(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function activateCampaign(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/activate`, {}, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function pauseCampaign(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/pause`, {}, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function resumeCampaign(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/resume`, {}, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function cancelCampaign(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/cancel`, {}, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getCampaignTimeline(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/timeline`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getCampaignAudiencePreview(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/audience-preview`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function listCampaignTouchpoints(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/touchpoints`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function addCampaignTouchpoint(profileName, id, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/touchpoints`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function updateCampaignTouchpoint(profileName, id, tpId, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .put(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/touchpoints/${tpId}`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function deleteCampaignTouchpoint(profileName, id, tpId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .delete(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/touchpoints/${tpId}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function approveCampaignTouchpoint(profileName, id, tpId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/touchpoints/${tpId}/approve`, {}, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function listCampaignSegments(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/segments`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function addCampaignSegment(profileName, id, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/segments`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function deleteCampaignSegment(profileName, id, segId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .delete(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/segments/${segId}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getCampaignAnalytics(profileName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/campaigns/${id}/analytics`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  // ── Audiences ──────────────────────────────────────────────────────────────

  function listAudiences(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/audiences`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function createAudience(profileName, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/audiences`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getAudience(profileName, audienceId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function updateAudience(profileName, audienceId, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .put(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function getAudiencePreview(profileName, audienceId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/preview`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function listAudienceMembers(profileName, audienceId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .get(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/members`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function addAudienceMember(profileName, audienceId, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/members`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function assignAudienceMember(profileName, audienceId, contactId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .put(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/members/${contactId}`, {}, {
          headers: { Authorization: token }
        })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function removeAudienceMember(profileName, audienceId, contactId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .delete(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/members/${contactId}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function addAudienceTagRule(profileName, audienceId, info) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .post(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/tag-rules`, info, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  function removeAudienceTagRule(profileName, audienceId, ruleId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      axios
        .delete(`${apiUrl}/api/profiles/${profileName}/audiences/${audienceId}/tag-rules/${ruleId}`, { headers: { Authorization: token } })
        .then(response => resolve({ success: true, data: response.data }))
        .catch(error => reject({ success: false, error }));
    });
  }

  // ── End Campaigns ──────────────────────────────────────────────────────────

  function engage_getEngagementLog(profileName, pg = 0, max = 20, tagged = "") {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${profileName}/community/engagement_log?pg=${pg}&max=${max}${
        tagged ? `&tagged=${tagged}` : ""
      }`;
      axios
        .get(url, { headers: { Authorization: token } })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_listPhysicalLocations(resourceName, childResourceName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/crm/profiles/${childResourceName}/physical_locations`;
      axios
        .get(url, {
          headers: { Authorization: token, Accept: "application/json" }
        })
        .then(response => {
          resolve({ success: true, data: response.data });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_addPhysicalLocation(
    resourceName,
    childResourceName,
    locationPayload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/crm/profiles/${childResourceName}/physical_locations`;
      axios
        .post(url, locationPayload, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
            Accept: "application/json"
          }
        })
        .then(response => {
          resolve({
            success: response.status === 201,
            data: response.data,
            location: response.headers.location
          });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_linkPhysicalLocation(
    resourceName,
    childResourceName,
    addressId,
    unitId
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/crm/profiles/${childResourceName}/physical_locations/${addressId}/${unitId}`;
      axios
        .post(url, null, {
          headers: {
            Authorization: token,
            Accept: "application/json"
          }
        })
        .then(response => {
          resolve({
            success: response.status === 201,
            data: response.data,
            location: response.headers.location
          });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_updatePhysicalLocation(
    resourceName,
    childResourceName,
    id,
    updatePayload
  ) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/crm/profiles/${childResourceName}/physical_locations/${id}`;
      axios
        .put(url, updatePayload, {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
            Accept: "application/json"
          }
        })
        .then(response => {
          resolve({
            success: response.status === 200,
            data: response.data
          });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  function crm_deletePhysicalLocation(resourceName, childResourceName, id) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = `${apiUrl}/api/profiles/${resourceName}/crm/profiles/${childResourceName}/physical_locations/${id}`;
      axios
        .delete(url, {
          headers: {
            Authorization: token
          }
        })
        .then(response => {
          resolve({ success: response.status === 204 });
        })
        .catch(error => {
          reject({ success: false, error });
        });
    });
  }

  return Object.freeze({
    getAuthentication,
    useDefaultAuthenticationToken,
    infoCheck,
    healthCheck,
    getJwtToken,
    isNonExpiredToken,
    isValidTokenForScope,
    clearAuthentication,
    getAnonymousAuthenticationToken,
    getAnonymousAuthenticationTokenFromHost,
    getAuthenticatedProfileForEdit,
    checkForCommunityContactByEmail,
    checkForProfile,
    getProfile,
    getProfileFromGPID,
    queryProfiles,
    profileAutocomplete,
    profileCommunityMemberAutocomplete,
    profileCommunityMemberOptions,
    getProfileCategories,
    activationMethod,
    getImageBase64,
    getImage,
    getResponsiveImage,
    getSquareImage,
    getFile,
    listEventsAtLocation,
    getAvailableEventTags,
    queryEvents,
    geoQueryEvents,
    listProfileEvents,
    getEvent,
    getEventByGroupTag,
    getPrintableEventMap,
    getEventMap,
    getEventMapSpacePlan,
    updateExistingEventMap,
    getEventMapPoints,
    getEventMapParticipants,
    createNewEventMapPoint,
    updateExistingEventMapPoint,
    deleteExistingEventMapPoint,
    getEventMapAmenities,
    createNewEventMapAmenity,
    updateExistingEventMapAmenity,
    deleteExistingEventMapAmenity,
    listExistingEventMapRoutes,
    createNewEventMapRoute,
    updateExistingEventMapRoute,
    deleteExistingEventMapRoute,
    getEventQrCode,
    getEventGalleryItems,
    submitEventGalleryItem,
    getEventGalleryItemSubmissions,
    approveSubmittedEventGalleryItem,
    rejectSubmittedEventGalleryItem,
    deleteEventGalleryItem,
    getEventWithToken,
    signUpToVolunteerForEvent,
    logTimeForVolunteerEvent,
    getEventVolunteerLog,
    logTimeForVolunteerManagementAsAdmin,
    listVolunteerManagementCheckins,
    getAllCategories,
    getFlattenedCategories,
    getProfileTypes,
    getProfileTypeGroups,
    getFlattenedProfileTypes,
    getFlattenedAmenities,
    getFlattenedPAUAmenities,
    getVendorTypes,
    getServiceCategories,
    listProfileMaps,
    listPrivateMaps,
    listPublicAndPrivateMaps,
    getProfileSummaryMap,
    getProfileSummaryMapWithToken,
    getProfileMap,
    getProfileMapPoints,
    getProfileMapWithToken,
    getProfileMapPointsWithToken,
    getPrivateProfileMap,
    getPrivateProfileMapPoints,
    getProfileMapForEdit,
    getProfileMapPointsForEdit,
    submitProfileMapPoint,
    getProfileMapSubmissions,
    approveSubmittedMapPoint,
    rejectSubmittedMapPoint,
    submitProfileMapComment,
    getProfileMapCommentSubmissions,
    approveProfileMapCommentSubmission,
    rejectProfileMapCommentSubmission,
    updateProfile,
    getAccountInfo,
    updateAccountInfo,
    uploadFile,
    uploadFileAnonymously,
    deleteFile,
    queryFileGallery,
    retrieveFileGalleryItem,
    retrieveUnownedFileGalleryItem,
    updateFileGalleryItem,
    deleteFileGalleryItem,
    addFileGalleryItem,
    forgotPassword,
    forgotPasswordVerify,
    resetPassword,
    registerNewProfile,
    registerNewProfileForAdmin,
    sendNewProfileAdminInvite,
    takeAdminProfileAction,
    listProfileAdmins,
    registerNewCommunityProfile,
    listProfileApiKeys,
    generateApiKey,
    deleteApiKey,
    verifyNewProfile,
    getLocationByPlaceId,
    getLocationsAutocomplete,
    getCitiesAutocomplete,
    geoLocationSearch,
    profileLocationSearch,
    reverseGeoLocationSearch,
    createNewEvent,
    updateExistingEvent,
    createNewMap,
    updateExistingMap,
    deleteExistingMap,
    createNewMapPoint,
    updateExistingMapPoint,
    deleteExistingMapPoint,
    queueMapImportJob,
    listMapImportJobs,
    queueMapExportJob,
    listMapExportJobs,
    downloadMapExportCSV,
    downloadMapSampleCSV,
    listExistingMapRoutes,
    createNewMapRoute,
    updateExistingMapRoute,
    deleteExistingMapRoute,
    deleteExistingEvent,
    event_publishEvent,
    event_unpublishEvent,
    activateUserProfile,
    createAccountForChildProfile,
    resendAccountActivationForChildProfile,
    sharePrivateLink,
    unsharePrivateLink,
    listProfileCommunities,
    queryCommunityMembers,
    inviteProfileCommunityMember,
    createSelfManagedMemberProfile,
    updateProfileCommunityMember,
    removeProfileCommunityMember,
    hasCommunityAccess,
    sendChildProfileResetEmail,
    resetChildProfilePassword,
    getProfileCommunity,
    getProfileCommunityWithToken,
    getProfileCommunityMember,
    getActiveProgramsForCommunityMember,
    getProfileCommunityMap,
    getProfileCommunityMapWithToken,
    updateProfileCommunityMap,
    queryProfileCommunity,
    queryProfileCommunityMap,
    queryProfileCommunityMapWithToken,
    listProfileCommunityMembersByType,
    queryProfileCommunityActivity,
    pinProfileCommunityActivity,
    unpinProfileCommunityActivity,
    forum_listForums,
    forum_createForum,
    forum_getForum,
    forum_updateForum,
    forum_deleteForum,
    forum_updateDigest,
    forum_listDiscussions,
    forum_getDiscussion,
    forum_createDiscussion,
    forum_updateDiscussion,
    forum_deleteDiscussion,
    forum_pinDiscussion,
    forum_unpinDiscussion,
    forum_listReplies,
    forum_createReply,
    forum_updateReply,
    forum_deleteReply,
    forum_listBookmarks,
    forum_addBookmark,
    forum_removeBookmark,
    forum_getSubscription,
    forum_updateSubscription,
    forum_listUpdates,
    portal_listPortals,
    portal_getPortal,
    portal_updatePortal,
    portal_getOverview,
    portal_listMembers,
    portal_revokeMember,
    portal_listInviteCandidates,
    portal_invite,
    portal_resendInvite,
    portal_getInvite,
    portal_acceptInvite,
    crm_updateContactAffiliation,
    crm_listContactAffiliations,
    crm_sendProfileClaimInvite,
    getProfileClaim,
    claimProfile,
    listOutgoingPointSubmissions,
    crm_listUpcomingVolunteerEvents,
    crm_listPreviousVolunteerEvents,
    crm_createNewVolunteerEvent,
    crm_updateExistingVolunteerEvent,
    crm_deleteExistingVolunteerEvent,
    updateExistingCommunity,
    requestCommunityAccess,
    approveCommunityAccess,
    rejectCommunityAccess,
    listPendingProfileCommunityMembers,
    // listCommunityManagedProfiles,
    getCommunityManagedProfile,
    deleteCommunityManagedProfile,
    createCommunityManagedProfile,
    updateCommunityManagedProfile,
    submitCommunityManagedProfileTransferRequest,
    transferCommunityManagedProfileToNewOwner,
    transferCommunityManagedProfileToExistingOwner,
    delegateCommunityManagedProfileContentRequest,
    delegateCommunityManagedProfileSubmission,
    submitCommunityManagedProfile,
    listProfilePosts,
    getProfilePostsGeoJson,
    getProfileMapsGeoJson,
    queryPosts,
    createPost,
    getPost,
    incrementPostView,
    updatePost,
    deletePost,
    getPostGallery,
    addGalleryItemToPost,
    updateExistingGalleryItemInPost,
    deleteExistingGalleryItemInPost,
    listMarketplaceCollections,
    getMarketplaceCollection,
    queryMarketplaceCollection,
    createMarketplaceCollection,
    updateMarketplaceCollection,
    deleteMarketplaceCollection,
    createProduct,
    getProduct,
    incrementProductView,
    updateProduct,
    deleteProduct,
    listProfileProducts,
    queryProducts,
    createProductSubmission,
    approveProductSubmission,
    rejectProductSubmission,
    listProductSubmissions,
    emailAddressAvailable,
    profileNameAvailable,
    getMetadata,
    getProfileLandingPage,
    updateProfileLandingPage,
    getUrlMetadata,
    getYoutubeMetadata,
    lookupDiscoverableContent,
    logClickRequest,
    viewProfileStatistics,
    viewMapStatistics,
    viewEventStatistics,
    viewPostStatistics,
    viewProductStatistics,
    listProfileEventParticipants,
    addProfileEventParticipant,
    editProfileEventParticipant,
    removeProfileEventParticipant,
    getRandomInt,
    submitPointToProfile,
    getProfilePointSubmissions,
    getProfilePointSubmission,
    queryProfilePointSubmissions,
    acceptOrRejectProfilePointSubmission,
    queryProfilePoints,
    deleteProfilePoint,
    listCommunityAccolades,
    createCommunityAccolade,
    editCommunityAccolade,
    deleteCommunityAccolade,
    applyCommunityAccoladeToMember,
    removeCommunityAccoladeFromMember,
    listFeatureGroups,
    listFeatureGroupSummaries,
    getSystemLayer,
    getSystemLayerGeoJson,
    getPublicSystemLayerGeoJson,
    putSystemLayer,
    getFeatureGroup,
    getFeatureGroups,
    getFeatureGroupGeoJson,
    getFeatureGroupFeatures,
    createFeatureGroup,
    updateFeatureGroup,
    deleteFeatureGroup,
    importFeatureGroupFeatures,
    createFeatureGroupFeature,
    updateFeatureGroupFeature,
    deleteFeatureGroupFeature,
    listPinLayers,
    getPublicPinLayers,
    getPinLayer,
    getPinLayerPoints,
    getPinLayerPoint,
    getPinLayerGeoJson,
    exportPinLayer,
    createPinLayer,
    updatePinLayer,
    deletePinLayer,
    queuePinLayerImportJob,
    listPinLayerImportJobs,
    getPinLayerActivityLog,
    createPinLayerPoint,
    updatePinLayerPoint,
    submitPinLayerCTA,
    deletePinLayerPoint,
    listPinLayerRoutes,
    createPinLayerRoute,
    getPinLayerRoute,
    updatePinLayerRoute,
    deletePinLayerRoute,
    listAssetGroupRoutes,
    createAssetGroupRoute,
    getAssetGroupRoute,
    updateAssetGroupRoute,
    deleteAssetGroupRoute,
    getIncidentTypeRegistry,
    listIncidents,
    getIncident,
    createIncident,
    updateIncident,
    deleteIncident,
    resolveIncident,
    reopenIncident,
    addIncidentComment,
    assignIncident,
    getIncidentTimeline,
    getPendingSubmissions,
    verifyIncidentSubmission,
    rejectIncidentSubmission,
    getIncidentTimeseries,
    getIncidentHeatmap,
    getIncidentClusters,
    generateIncidentShareLink,
    exportIncidentsCsv,
    exportIncidentsGeoJson,
    sendIncidentEmailReport,
    sweepStaleIncidents,
    requestIncidentUploadToken,
    communityRequestIncidentUploadToken: requestIncidentUploadToken,
    uploadIncidentPhoto,
    uploadPublicIncidentPhoto,
    communityUploadIncidentPhoto: uploadPublicIncidentPhoto,
    submitPublicIncident,
    communitySubmitPublicIncident: submitPublicIncident,
    getPublicIncidentStatus,
    generatePublicWorksPortalToken,
    getIncidentManagementConfiguration,
    updateIncidentManagementConfiguration,
    getCommunityPubliclySubmittableTypes,
    queueCommunityListingImportJob,
    listCommunityListingImportJobs,
    downloadCommunityImportSampleCSV,
    queueCommunityListingExportJob,
    listCommunityListingExportJobs,
    downloadCommunityExportCSV,
    listProfileNotifications,
    contributeMediaSubmissionToStoryMapPoint,
    listStoryMapPointMediaSubmissions,
    approveStoryMapPointMediaSubmission,
    rejectStoryMapPointMediaSubmission,
    buildRouteWithDirections,
    getOptimizedRoute,
    crm_uploadFileForStaging,
    crm_listUploadedJobStatuses,
    crm_getUploadJobStatus,
    crm_getUploadStagedData,
    crm_stageUpload,
    crm_listStagedRecords,
    crm_getStagedRecord,
    crm_publishStagedRecords,
    crm_discardStagedRecords,
    crm_reindexProfiles,
    crm_reindexAddresses,
    crm_reindexParcels,
    crm_reindexContacts,
    crm_listDataMappers,
    crm_listImportableFields,
    crm_createDataMapper,
    crm_updateDataMapper,
    crm_deleteDataMapper,
    listLocatorConfigurations,
    getLocatorConfiguration,
    createLocatorConfiguration,
    updateLocatorConfiguration,
    deleteLocatorConfiguration,
    crm_publishDataImport,
    crm_listPublishedDataImports,
    crm_getPublishedDataImport,
    crm_publicDataQuery,
    crm_dataQuery,
    crm_getCrmBusinessDashboardView,
    crm_analyzeBusinessInventory,
    crm_getBusinessGeoJson,
    crm_getOrganizationGeoJson,
    shareDataAnalysis,
    getSharedDataAnalysis,
    crm_profileExport,
    crm_profileBulkActions,
    crm_profileMergeOperation,
    getUsedProfileTags,
    crm_getProfile,
    crm_getProfileHealth,
    crm_profileAutocomplete,
    crm_createProfile,
    crm_updateProfile,
    crm_archiveProfile,
    crm_blockProfileAccess,
    crm_unblockProfileAccess,
    crm_initiateWorkflow,
    crm_sendWorkflowResponse,
    crm_getPublicChildProfile,
    crm_updatePublicChildProfile,
    crm_enhancePublicChildProfile,
    crm_getUnifiedConfiguration,
    crm_updateUnifiedConfiguration,
    crm_resetWorkflowConfiguration,
    crm_unifiedProfileWorkflowInitiation,
    crm_getOnboardingStatistics,
    crm_queryChildProfileJournalEntry,
    crm_addChildProfileJournalEntry,
    crm_queryContactJournalEntries,
    crm_addContactJournalEntry,
    cms_getRELocatorGeoJson,
    cms_getREBuildingsGeoJson,
    cms_reListingQuery,
    cms_getREListing,
    cms_getLocatorGeoJson,
    getPublicLocatorConfiguration,
    cms_getLocatorBuildingIndex,
    cms_getLocatorBuildingProfiles,
    cms_getChildProfile,
    crm_contactExport,
    crm_getBusinessContactsFromBusinesses,
    crm_getBusinessContactsFromContactIds,
    crm_listProfilePOC,
    crm_updateContact,
    crm_createContact,
    crm_deleteContact,
    crm_linkContactToProfile,
    crm_unlinkContactFromProfile,
    crm_setContactAsPrimary,
    crm_listPhysicalLocations,
    crm_addPhysicalLocation,
    crm_linkPhysicalLocation,
    crm_updatePhysicalLocation,
    crm_deletePhysicalLocation,
    listTemplates,
    queryTemplates,
    createTemplate,
    getTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    getCommunityEmailConfiguration,
    updateCommunityEmailConfiguration,
    getCommunitySmsConfiguration,
    updateCommunitySmsConfiguration,
    getCommunityLandingPageConfiguration,
    updateCommunityLandingPageConfiguration,
    disableCommunityLandingPageConfiguration,
    getCommunityBrandingConfiguration,
    updateCommunityBrandingConfiguration,
    deleteCommunityBrandingConfiguration,
    getCommunityMapConfiguration,
    updateCommunityMapConfiguration,
    resetCommunityMapConfiguration,
    getDefaultMapStyles,
    listSentEmails,
    getSingleEmailPreview,
    getSingleEmailPreviewLink,
    getSingleEmailStatistics,
    getSingleEmailClickStatistics,
    getEmailEngagementDetails,
    querySentEmails,
    getEmailEngagementStatistics,
    sendEmail,
    sendSms,
    listCommunityContacts,
    getCommunityContactsTags,
    getCommunityContact,
    queryCommunityContacts,
    lookupCommunityContacts,
    resolveCommunityContactsByTag,
    createCommunityContact,
    updateCommunityContact,
    deleteCommunityContact,
    communityContactOptIntoSms,
    communityContactAdminOptIntoSms,
    communityContactAdminOptOut,
    communityContactConsentLog,
    communityContactUpdateBlockedCategories,
    smsProvisionGetStatus,
    smsProvisionSync,
    smsProvisionCreateSubaccount,
    smsProvisionCreateApiKey,
    smsProvisionCreateCustomerProfile,
    smsProvisionCreateTrustProduct,
    smsProvisionSubmitBrandRegistration,
    smsProvisionCreateMessagingService,
    smsProvisionPurchaseNumber,
    smsProvisionCreateCampaign,
    smsProvisionActivate,
    smsProvisionStart,
    listSentSms,
    querySentSms,
    getSingleSmsStatistics,
    getSmsEngagementDetails,
    communityUnsubscribeFromEmails,
    initiateContactVerification,
    contactVerification,
    communityContactBulkActions,
    getExistingManagedEvent,
    listManagedEvents,
    listNextManagedEvents,
    listPreviousManagedEvents,
    createNewManagedEvent,
    copyExistingManagedEvent,
    updateExistingManagedEvent,
    deleteExistingManagedEvent,
    archiveExistingManagedEvent,
    listManagedEventParticipants,
    listManagedEventSchedule,
    getManagedEventScheduledEvent,
    addEventToManagedEventSchedule,
    removeEventFromManagedEventSchedule,
    updateEventOnManagedEventSchedule,
    getManagedEventParticipantGroups,
    getManagedEventParticipantGroupParticipants,
    eventParticipantGroupParticipantBulkActions,
    exportManagedEventGroupParticipants,
    exportManagedEventGroupSubmissions,
    getManagedEventParticipantGroupConfiguration,
    updateManagedEventParticipantGroupConfiguration,
    createManagedEventParticipantGroupParticipant,
    updateManagedEventParticipantGroupParticipant,
    deleteManagedEventParticipantGroupParticipant,
    managedEventParticipantSubmissionWithPOC,
    managedEventParticipantSubmissionNoPOC,
    getManagedEventParticipantGroupSubmissions,
    approveEventParticipantGroupSubmission,
    rejectEventParticipantGroupSubmission,
    getVendorScheduleConfiguration,
    market_getNextMarketEvent,
    market_getMarketConfiguration,
    market_updateMarketConfiguration,
    market_getMarketResources,
    market_getMarketApplication,
    market_getExistingMarketApplication,
    market_submitMarketApplication,
    market_resubmitMarketApplication,
    market_sendActivationEmail,
    market_approveMarketApplication,
    market_rejectMarketApplication,
    market_refuseMarketApplication,
    market_getMarketVendorsOfType,
    market_getMarketEventConfiguration,
    market_updateMarketEventConfiguration,
    market_publishMarketEvent,
    market_unpublishMarketEvent,
    market_getVendorSchedule,
    market_getVendorScheduleMap,
    market_getVendorScheduleForDate,
    market_getVendorScheduleMapForDate,
    market_getSingleVendorSchedule,
    market_exportVendorScheduleForDate,
    market_getAvailableVendorsForDate,
    market_addVendorToSchedule,
    market_putVendorOnVendorSchedule,
    market_addVendorToVendorSchedule,
    market_removeVendorFromVendorSchedule,
    market_removeVendorFromScheduledDate,
    market_markVendorScheduleSpaceAsUnavailable,
    market_publishMarketEventSchedule,
    market_sendManagedEventMarketEmailSequence,
    market_getUpcomingMarketEvents,
    market_getAllMarketEvents,
    market_getUpcomingMarketEventsForVendor,
    market_cancelMarketEventReservation,
    market_retrieveMyUpcomingMarketEvents,
    market_getBookingConfiguration,
    market_getPaymentIntent,
    market_getMarketPayments,
    market_getVendorPayments,
    market_getSingleBookingPaymentDetails,
    market_getBookingPaymentDetailsForProfile,
    market_getProducts,
    market_createCreditProduct,
    market_updateCreditProduct,
    market_deleteCreditProduct,
    market_getCreditPaymentIntent,
    market_getVendorPaymentDetailsForSession,
    market_queryMarketPayments,
    listProfileSpacePlans,
    listTemplateForms,
    createFormFromTemplate,
    listForms,
    queryForms,
    formsAutocomplete,
    getForm,
    createForm,
    updateForm,
    deleteForm,
    copyForm,
    queryIcons,
    listSurveys,
    querySurveys,
    surveysAutocomplete,
    getSurvey,
    createSurvey,
    updateSurvey,
    copySurvey,
    deleteSurvey,
    listTemplateSurveys,
    createSurveyFromTemplate,
    startPublicSurvey,
    submitPublicSurveyResponse,
    startPrivateSurvey,
    submitPrivateSurveyResponse,
    listSurveyHistory,
    getSingleSurveyHistory,
    listSurveyResponses,
    deleteSurveyResponse,
    exportSurveyResponses,
    listDocuments,
    queryDocuments,
    createDocument,
    getDocuments,
    updateDocument,
    deleteDocument,
    listTemplateEvents,
    createEventFromTemplate,
    eventsAutocomplete,
    getVolunteerManagementConfiguration,
    updateVolunteerManagementConfiguration,
    engage_getEngagementLog,
    reporting_getReportableDescriptor,
    reporting_listTemplates,
    reporting_createTemplate,
    reporting_updateTemplate,
    reporting_deleteTemplate,
    reporting_runTemplate,
    reporting_listReports,
    reporting_createReport,
    reporting_getReport,
    reporting_deleteReport,
    reporting_regenerateReport,
    reporting_downloadPdf,
    reporting_thumbnailUrl,
    reporting_emailReport,
    listDbConnectors,
    getDbConnector,
    createDbConnector,
    updateDbConnector,
    deleteDbConnector,
    getDbConnectorAccessUrl,
    getOvertureCatalog,
    getOvertureFiles,
    importOverturePlace,
    analyzeIsochrones,
    getDatasetFiles,
    listCampaigns,
    getCampaign,
    createCampaign,
    updateCampaign,
    activateCampaign,
    pauseCampaign,
    resumeCampaign,
    cancelCampaign,
    getCampaignTimeline,
    getCampaignAudiencePreview,
    listCampaignTouchpoints,
    addCampaignTouchpoint,
    updateCampaignTouchpoint,
    deleteCampaignTouchpoint,
    approveCampaignTouchpoint,
    listCampaignSegments,
    addCampaignSegment,
    deleteCampaignSegment,
    getCampaignAnalytics,
    listAudiences,
    createAudience,
    getAudience,
    updateAudience,
    getAudiencePreview,
    listAudienceMembers,
    addAudienceMember,
    assignAudienceMember,
    removeAudienceMember,
    getAudienceSubscriptionConfig,
    updateAudienceSubscriptionConfig,
    subscribeToAudience,
    addAudienceTagRule,
    removeAudienceTagRule,
    apiUrl,
  });

  function reporting_getReportableDescriptor(profileName, pinLayerId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/map/pin_layers/" + pinLayerId + "/reportable";
      axios.get(url, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_listTemplates(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/report_templates";
      axios.get(url, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_createTemplate(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/report_templates";
      axios.post(url, payload, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_updateTemplate(profileName, templateId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/report_templates/" + templateId;
      axios.put(url, payload, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_deleteTemplate(profileName, templateId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/report_templates/" + templateId;
      axios.delete(url, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_runTemplate(profileName, templateId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/report_templates/" + templateId + "/run";
      axios.post(url, {}, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_listReports(profileName) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/reports";
      axios.get(url, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_createReport(profileName, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/reports";
      axios.post(url, payload, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_getReport(profileName, reportId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/reports/" + reportId;
      axios.get(url, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_deleteReport(profileName, reportId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/reports/" + reportId;
      axios.delete(url, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_regenerateReport(profileName, reportId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/reports/" + reportId + "/regenerate";
      axios.post(url, {}, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }

  function reporting_downloadPdf(profileName, reportId) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/reports/" + reportId + ".pdf";
      axios
        .get(url, {
          responseType: "blob",
          headers: { Authorization: token, Accept: "application/pdf" }
        })
        .then(response => resolve(response.data))
        .catch(error => reject({ success: false, error }));
    });
  }

  function reporting_thumbnailUrl(profileName, reportId) {
    return apiUrl + "/api/profiles/" + profileName + "/community/reports/" + reportId + "/thumbnail";
  }

  function reporting_emailReport(profileName, reportId, payload) {
    let token;
    let storedAuth = cookies.get("authentication", { doNotParse: true });
    if (storedAuth != null) {
      var authentication = JSON.parse(storedAuth);
      token = "Bearer " + authentication.token;
    }
    return new Promise(function(resolve, reject) {
      const url = apiUrl + "/api/profiles/" + profileName + "/community/reports/" + reportId + "/email";
      axios.post(url, payload, { headers: { Authorization: token } })
        .then(r => resolve(r.data))
        .catch(e => reject({ success: false, error: e }));
    });
  }
}
export default API;
